
import { useAuth0 } from '@auth0/auth0-react';
import {
  AlertTriangle,
  ClipboardList,
  CreditCard,
  Download,
  Eye,
  FileText,
  Gift,
  Crown,
  HeartPulse,
  Link,
  Loader2,
  Mail,
  MapPin,
  Mic,
  Phone,
  Pill,
  Printer,
  Shield,
  Share2,
  Stethoscope,
  Trash2,
  UserPlus,
  Users,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useAppointmentModalStore } from '../../../stores/appointmentModalStore';
import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BillingModule } from '../../../components/BillingModule';
import { PatientAiSummaryCard } from '../../../components/PatientAiSummaryCard';
import { PatientDocumentsModule } from '../../../components/PatientDocumentsModule';
import { PatientMedicalFormsModule } from '../../../components/PatientMedicalFormsModule';
import { PatientManualTestsModule } from '../../../components/PatientManualTestsModule';
import { PatientNotesModule } from '../../../components/PatientNotesModule';
import { PatientPortalAccessModule } from '../../../components/PatientPortalAccessModule';
import { PatientCoordinationModule } from '../../../components/PatientCoordinationModule';
import { PatientTreatmentPlansModule } from '../../../components/PatientTreatmentPlansModule';
import { PatientVaccinationsModule } from '../../../components/PatientVaccinationsModule';
import { AddressFields } from '../../../components/AddressFields';
import InitialAvatar from '../../../components/InitialAvatar';
import { API_BASE, AUTH_AUDIENCE } from '../../../config/env';
import { AVATAR_ALLOWED_TYPES, AVATAR_MAX_SOURCE_BYTES, optimizeAvatarFile } from '../../../lib/avatarUpload';
import { cn } from '../../../utils/cn';
import { useApiClient } from '../../../lib/apiClient';
import { SelectField, TextField } from '../../../ui/fields';
import { useI18n } from '../../../i18n/LanguageContext';
import { formatMoney } from '../../../i18n/orgFormat';
import {
  AllergyEntry,
  MedicationEntry,
  PatientRow,
  ProblemEntry,
  Sex,
  formatAllergies,
  formatMedications,
  formatProblems,
  statusColors,
  statusLabels,
} from '../domain';
import { PATIENT_FILE_PERMISSION_GROUPS, permissionCodeMap } from '../../../lib/permissionCatalog';

const COMMON_ALLERGIES = [
  'Penicillin', 'Peanuts', 'Latex', 'Sulfa Drugs', 'Aspirin', 'Shellfish', 'Codeine', 'Amoxicillin',
  'Ibuprofen', 'Eggs', 'Milk', 'Soy', 'Wheat', 'Tree Nuts', 'Morphine', 'Iodine', 'Bee Stings',
  'Dust Mites', 'Pollen', 'Mold', 'Cats', 'Dogs', 'NSAIDs', 'Contrast Dye', 'Tetracycline'
];

const COMMON_PROBLEMS = [
  'Hypertension', 'Type 2 Diabetes', 'Asthma', 'Hyperlipidemia', 'GERD', 'Anxiety', 'Depression',
  'Obesity', 'Osteoarthritis', 'Hypothyroidism', 'Migraine', 'COPD', 'Coronary Artery Disease',
  'Atrial Fibrillation', 'Chronic Kidney Disease', 'Heart Failure', 'Sleep Apnea', 'Back Pain',
  'Eczema', 'Psoriasis', 'Rheumatoid Arthritis', 'Vitamin D Deficiency', 'Anemia', 'Allergic Rhinitis'
];

const COMMON_MEDICATIONS = [
  'Lisinopril', 'Metformin', 'Atorvastatin', 'Amlodipine', 'Albuterol', 'Levothyroxine', 'Omeprazole',
  'Gabapentin', 'Losartan', 'Sertraline', 'Furosemide', 'Hydrochlorothiazide', 'Metoprolol', 'Pantoprazole',
  'Simvastatin', 'Ibuprofen', 'Acetaminophen', 'Prednisone', 'Trazodone', 'Fluticasone', 'Montelukast',
  'Escitalopram', 'Rosuvastatin', 'Bupropion', 'Amoxicillin', 'Azithromycin', 'Tramadol', 'Insulin Glargine'
];

const appointmentStatusLabels: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  PORTAL_BOOKED: 'Portal booked',
  CHECKED_IN: 'Checked-in',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No-show',
};

function translateAppointmentStatus(tr: (phrase: string) => string, status?: string | null) {
  if (!status) return '-';
  return tr(appointmentStatusLabels[status] || status);
}

function translateAppointmentTypeName(tr: (phrase: string) => string, name?: string | null) {
  return name ? tr(name) : tr('Appointment');
}

function getAppointmentDisplayNames(appt: {
  appointmentType?: { name?: string | null } | null;
  billingService?: { name?: string | null } | null;
}) {
  const sessionTypeName = appt.appointmentType?.name?.trim() || '';
  const bookedServiceName = appt.billingService?.name?.trim() || '';
  return {
    primaryName: bookedServiceName || sessionTypeName || 'Appointment',
    secondaryName: bookedServiceName && bookedServiceName !== sessionTypeName ? sessionTypeName : '',
  };
}

function patientAppointmentClassMeta(appt: {
  appointmentType?: { serviceDocumentation?: Record<string, unknown> | null } | null;
  notes?: string | null;
}) {
  const docs = appt.appointmentType?.serviceDocumentation && typeof appt.appointmentType.serviceDocumentation === 'object'
    ? appt.appointmentType.serviceDocumentation as Record<string, any>
    : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  return {
    isClass: Boolean(meta.isClass) || /class booking:/i.test(String(appt.notes || '')),
    classCapacity: Number(meta.classCapacity || 8),
  };
}

type DetailProps = {
  patient: PatientRow;
  appointments?: Array<{
    id: string;
    title?: string | null;
    startTime: string;
    endTime: string;
    status: string;
    type?: string | null;
    appointmentType?: { id: string; name: string; color?: string; isTelemed?: boolean; serviceDocumentation?: Record<string, unknown> };
    billingService?: { id: string; name: string } | null;
    provider?: { id: string; name: string };
    encounterId?: string | null;
    notes?: string | null;
  }>;
  permissionCodes?: string[];
  onPatientUpdated: (p: PatientRow) => void;
};
export function PatientDetail({ patient, appointments = [], permissionCodes = [], onPatientUpdated }: DetailProps) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { tr } = useI18n();
  const [tab, setTab] = useState<
    | 'Overview'
    | 'Documents'
    | 'Letters'
    | 'Vaccinations'
    | 'Notes'
    | 'Medical Forms'
    | 'Manual Tests'
    | 'Treatment Plans'
    | 'Elenthor'
    | 'Coordination'
    | 'Portal Access'
    | 'Visits'
    | 'Appointments'
    | 'Telemedicine'
    | 'Invoices'
  >('Overview');
  const [searchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const requestedNoteId = searchParams.get('noteId');
    if (requestedNoteId) {
      setTab('Notes');
      return;
    }

    const requestedTab = searchParams.get('tab');
    if (!requestedTab) return;
    const allowedTabs = new Set([
      'Overview',
      'Documents',
      'Letters',
      'Vaccinations',
      'Notes',
      'Medical Forms',
      'Manual Tests',
      'Treatment Plans',
      'Elenthor',
      'Coordination',
      'Portal Access',
      'Visits',
      'Appointments',
      'Telemedicine',
      'Invoices',
    ]);
    if (allowedTabs.has(requestedTab)) {
      setTab(requestedTab as typeof tab);
    }
  }, [searchParams]);

  async function updatePatient(
    partial: Partial<PatientRow>,
    nextRaw?: Partial<Pick<PatientRow, 'allergiesRaw' | 'problemsRaw' | 'medicationsRaw'>>,
  ) {
    try {
      let token = '';
      try {
        token = await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
      } catch (err: any) {
        if (err?.error === 'login_required') {
          await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
          return;
        }
        throw err;
      }
      const payload = {
        mrn: partial.mrn ?? patient.mrn,
        firstName: partial.firstName ?? patient.firstName,
        middleName: partial.middleName ?? patient.middleName ?? undefined,
        lastName: partial.lastName ?? patient.lastName,
        avatarUrl: partial.avatarUrl ?? patient.avatarUrl ?? undefined,
        dob: partial.dob ?? patient.dob,
        sex: partial.sex ?? patient.sex,
        genderIdentity: partial.genderIdentity ?? patient.genderIdentity ?? undefined,
        phone: partial.phone ?? patient.phone ?? undefined,
        phoneSecondary: partial.phoneSecondary ?? patient.phoneSecondary ?? undefined,
        email: partial.email ?? patient.email ?? undefined,
        status: partial.status ?? patient.status,
        city: partial.city ?? patient.city ?? undefined,
        state: partial.state ?? patient.state ?? undefined,
        postalCode: partial.postalCode ?? patient.postalCode ?? undefined,
        insuranceProvider: partial.insuranceProvider ?? patient.insuranceProvider ?? undefined,
        insuranceMemberId: partial.insuranceMemberId ?? patient.insuranceMemberId ?? undefined,
        insuranceGroupNumber: partial.insuranceGroupNumber ?? patient.insuranceGroupNumber ?? undefined,
        emergencyContactName: partial.emergencyContactName ?? patient.emergencyContactName ?? undefined,
        emergencyContactPhone: partial.emergencyContactPhone ?? patient.emergencyContactPhone ?? undefined,
        primaryProviderId: partial.primaryProviderId ?? patient.primaryProviderId ?? undefined,
        preferredPharmacyId: Object.prototype.hasOwnProperty.call(partial, 'preferredPharmacyId')
          ? partial.preferredPharmacyId ?? undefined
          : patient.preferredPharmacyId ?? undefined,
        flags: partial.flags ?? patient.flags ?? [],
        tags: [],
        allergies: nextRaw?.allergiesRaw ?? patient.allergiesRaw ?? [],
        problems: nextRaw?.problemsRaw ?? patient.problemsRaw ?? [],
        medications: nextRaw?.medicationsRaw ?? patient.medicationsRaw ?? [],
      };
      const res = await fetch(`${API_BASE}/patients/${patient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed ${res.status}`);
      const nextPatient: PatientRow = {
        ...patient,
        ...partial,
        preferredPharmacyId: Object.prototype.hasOwnProperty.call(partial, 'preferredPharmacyId')
          ? partial.preferredPharmacyId ?? null
          : patient.preferredPharmacyId ?? null,
        preferredPharmacyName: Object.prototype.hasOwnProperty.call(partial, 'preferredPharmacyName')
          ? partial.preferredPharmacyName ?? null
          : patient.preferredPharmacyName ?? null,
        allergiesRaw: payload.allergies as AllergyEntry[],
        problemsRaw: payload.problems as ProblemEntry[],
        medicationsRaw: payload.medications as MedicationEntry[],
        allergies: formatAllergies(payload.allergies as AllergyEntry[]),
        problems: formatProblems(payload.problems as ProblemEntry[]),
        medications: formatMedications(payload.medications as MedicationEntry[]),
        address: [payload.city, payload.state, payload.postalCode].filter(Boolean).join(', '),
      };
      onPatientUpdated(nextPatient);
    } catch (err) {
      console.error(err);
    }
  }

  const addAllergy = (name: string) => {
    if (!name.trim()) return;
    const updated = [...patient.allergiesRaw, { name }];
    updatePatient({}, { allergiesRaw: updated });
  };
  const removeAllergy = (idx: number) => {
    const updated = patient.allergiesRaw.filter((_, i) => i !== idx);
    updatePatient({}, { allergiesRaw: updated });
  };
  const addProblem = (name: string) => {
    if (!name.trim()) return;
    const updated = [...patient.problemsRaw, { name }];
    updatePatient({}, { problemsRaw: updated });
  };
  const removeProblem = (idx: number) => {
    const updated = patient.problemsRaw.filter((_, i) => i !== idx);
    updatePatient({}, { problemsRaw: updated });
  };
  const addMedication = (name: string) => {
    if (!name.trim()) return;
    const updated = [...patient.medicationsRaw, { name }];
    updatePatient({}, { medicationsRaw: updated });
  };
  const removeMedication = (idx: number) => {
    const updated = patient.medicationsRaw.filter((_, i) => i !== idx);
    updatePatient({}, { medicationsRaw: updated });
  };

  const appointmentInsights = useMemo(() => {
    const list = appointments || [];
    const now = Date.now();
    const upcoming = list
      .filter((a) => new Date(a.startTime).getTime() > now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    const past = list
      .filter((a) => new Date(a.startTime).getTime() <= now)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
    const counts: Record<string, number> = {};
    list.forEach((a) => {
      const d = new Date(a.startTime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    const visitsByMonth = Object.entries(counts)
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([key, value]) => {
        const [y, m] = key.split('-').map((n) => Number(n));
        return { label: new Date(y, m - 1).toLocaleString([], { month: 'short' }), value };
      });
    const lastVisitAppt = past.find((a) => a.status === 'COMPLETED') ?? past[0] ?? null;
    const nextVisitAppt = upcoming[0] ?? null;
    return { upcoming, past, visitsByMonth, lastVisitAppt, nextVisitAppt };
  }, [appointments]);

  const tabPermissionMap = permissionCodeMap(PATIENT_FILE_PERMISSION_GROUPS);
  const allTabs = [
    'Overview',
    'Documents',
    'Letters',
    'Vaccinations',
    'Notes',
    'Medical Forms',
    'Manual Tests',
    'Treatment Plans',
    'Elenthor',
    'Coordination',
    'Visits',
    'Appointments',
    'Telemedicine',
    'Invoices',
    'Portal Access',
  ] as const;
  const visibleTabs = useMemo(() => {
    const permissions = new Set(permissionCodes);
    if (!permissions.size) return allTabs;
    return allTabs.filter((tab) => {
      const required = tabPermissionMap[tab] || [];
      if (!required.length) return true;
      return required.some((code) => permissions.has(code));
    });
  }, [allTabs, permissionCodes, tabPermissionMap]);

  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(visibleTabs[0] || 'Overview');
    }
  }, [tab, visibleTabs]);

  const formattedLastVisit = appointmentInsights.lastVisitAppt?.startTime
    ? new Date(appointmentInsights.lastVisitAppt.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
    : patient.lastVisit
      ? new Date(patient.lastVisit).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
      : null;
  const formattedNextVisit = appointmentInsights.nextVisitAppt
    ? new Date(appointmentInsights.nextVisitAppt.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
    : patient.upcoming || null;
  const formattedDob = patient.dob ? new Date(patient.dob).toLocaleDateString() : patient.dob;
  const translatedSex =
    patient.sex === 'M' ? tr('Male') : patient.sex === 'F' ? tr('Female') : patient.sex === 'O' ? tr('Other') : patient.sex;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex gap-3">
          <button
            type="button"
            className="h-12 w-12 rounded-full"
            onClick={() => setEditing(true)}
            title={tr('Upload photo')}
          >
            <InitialAvatar name={`${patient.firstName} ${patient.lastName}`} src={patient.avatarUrl} alt={tr('Patient avatar')} className="h-12 w-12 text-lg" />
          </button>
          <div>
            <p className="text-lg font-semibold text-ink">{`${patient.firstName} ${patient.lastName}`}</p>
            <p className="text-sm text-slate-500">
              MRN {patient.mrn} | {patient.age} {tr('yrs')} | {tr('DOB')} {formattedDob} | {translatedSex}
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Phone size={14} /> {patient.phone}
              <Mail size={14} /> {patient.email}
              <MapPin size={14} /> {patient.address}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn('pill', statusColors[patient.status])}>{tr(statusLabels[patient.status])}</span>
          <button className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-ink" onClick={() => setEditing(true)}>
            {tr('Edit')}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
        <span className="pill pill-info">{formattedLastVisit ? `${tr('Last visit')} ${formattedLastVisit}` : tr('No visits yet')}</span>
        {formattedNextVisit && <span className="pill pill-success">{tr('Next')}: {formattedNextVisit}</span>}
        <span className="pill pill-info">{tr('Provider')}: {patient.provider}</span>
        <FlagIcons flags={patient.flags} />
      </div>
      <div className="border-b border-slate-100 flex gap-4 text-sm">
        {visibleTabs.map((t) => (
          <button
            key={t}
            className={cn(
              'pb-2',
              tab === t ? 'text-ink border-b-2 border-ink font-semibold' : 'text-slate-500 hover:text-ink',
            )}
            onClick={() => setTab(t as any)}
          >
            {tr(t)}
          </button>
        ))}
      </div>
      {tab === 'Overview' && (
        <OverviewTab
          patient={patient}
          appointmentInsights={appointmentInsights}
          onAddAllergy={addAllergy}
          onRemoveAllergy={removeAllergy}
          onAddProblem={addProblem}
          onRemoveProblem={removeProblem}
          onAddMedication={addMedication}
          onRemoveMedication={removeMedication}
        />
      )}
      {tab === 'Documents' && <PatientDocumentsModule patientId={patient.id} />}
      {tab === 'Letters' && <PatientLettersModule patient={patient} />}
      {tab === 'Vaccinations' && <PatientVaccinationsModule patientId={patient.id} />}
      {tab === 'Notes' && <PatientNotesModule patientId={patient.id} preferredNoteId={searchParams.get('noteId')} />}
      {tab === 'Medical Forms' && <PatientMedicalFormsModule patientId={patient.id} patientEmail={patient.email} />}
      {tab === 'Manual Tests' && <PatientManualTestsModule patientId={patient.id} />}
      {tab === 'Treatment Plans' && <PatientTreatmentPlansModule patientId={patient.id} />}
      {tab === 'Elenthor' && <ServiceTimelineTab patientId={patient.id} />}
      {tab === 'Coordination' && <PatientCoordinationModule patient={{ id: patient.id, firstName: patient.firstName, lastName: patient.lastName, mrn: patient.mrn }} />}
      {tab === 'Portal Access' && <PatientPortalAccessModule patientId={patient.id} />}
      {tab === 'Visits' && <PatientVisitsTab patientId={patient.id} />}
      {tab === 'Appointments' && <AppointmentsTab appointments={appointments} patientId={patient.id} />}
      {tab === 'Telemedicine' && <TelemedTab appointments={appointments} patientId={patient.id} />}
      {tab === 'Invoices' && <BillingModule patientId={patient.id} />}
      <EditPatientModal open={editing} patient={patient} onClose={() => setEditing(false)} onSaved={(p) => onPatientUpdated(p)} />
    </div>
  );
}

export function FlagIcons({ flags }: { flags: PatientRow['flags'] }) {
  const { tr } = useI18n();
  return (
    <div className="flex items-center gap-2 text-slate-500">
      {flags.includes('allergy') && (
        <span title={tr('Allergies')}>
          <AlertTriangle size={14} className="text-amber-500" />
        </span>
      )}
      {flags.includes('high-risk') && (
        <span title={tr('High risk')}>
          <Shield size={14} className="text-rose-500" />
        </span>
      )}
      {flags.includes('vip') && (
        <span title="VIP">
          <Crown size={14} className="text-purple-500" />
        </span>
      )}
    </div>
  );
}
function OverviewTab({
  patient,
  appointmentInsights,
  onAddAllergy,
  onRemoveAllergy,
  onAddProblem,
  onRemoveProblem,
  onAddMedication,
  onRemoveMedication,
}: {
  patient: PatientRow;
  appointmentInsights: {
    upcoming: NonNullable<DetailProps['appointments']>;
    past: NonNullable<DetailProps['appointments']>;
    visitsByMonth: Array<{ label: string; value: number }>;
  };
  onAddAllergy: (name: string) => void;
  onRemoveAllergy: (idx: number) => void;
  onAddProblem: (name: string) => void;
  onRemoveProblem: (idx: number) => void;
  onAddMedication: (name: string) => void;
  onRemoveMedication: (idx: number) => void;
}) {
  const { openModal } = useAppointmentModalStore();
  const { tr } = useI18n();
  const upcomingList = appointmentInsights.upcoming.slice(0, 3);
  const timelineItems = [...appointmentInsights.upcoming.slice(0, 1), ...appointmentInsights.past.slice(0, 3)];
  const visitsByMonth = appointmentInsights.visitsByMonth;
  const chartOptions: Highcharts.Options = {
    chart: { type: 'area', height: 160, backgroundColor: 'transparent', margin: [5, 0, 20, 0] },
    title: { text: undefined },
    xAxis: { categories: visitsByMonth.map(d => d.label), lineColor: 'transparent', tickColor: 'transparent', labels: { style: { color: '#94a3b8', fontSize: '10px' } } },
    yAxis: { title: { text: undefined }, gridLineColor: '#f1f5f9', labels: { enabled: false } }, // Hide Y
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: { area: { marker: { enabled: false }, fillOpacity: 0.1, lineWidth: 2, color: '#0ea5e9' } },
    series: [{ type: 'area', name: tr('Visits'), data: visitsByMonth.map(d => d.value) }]
  };

  return (
    <div className="space-y-3">
      <PatientAiSummaryCard patientId={patient.id} />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <ManageListCard
          title={tr('Allergies')}
          icon={<AlertTriangle size={16} className="text-amber-500" />}
          items={patient.allergies}
          options={COMMON_ALLERGIES}
          onAdd={onAddAllergy}
          onDelete={onRemoveAllergy}
          placeholder={tr('Add allergy')}
        />
        <ManageListCard
          title={tr('Problems')}
          icon={<HeartPulse size={16} className="text-rose-500" />}
          items={patient.problems}
          options={COMMON_PROBLEMS}
          onAdd={onAddProblem}
          onDelete={onRemoveProblem}
          placeholder={tr('Add problem')}
        />
        <ManageListCard
          title={tr('Medications')}
          icon={<Pill size={16} className="text-emerald-600" />}
          items={patient.medications}
          options={COMMON_MEDICATIONS}
          onAdd={onAddMedication}
          onDelete={onRemoveMedication}
          placeholder={tr('Add medication')}
        />
        <InfoCard
          title={tr('Upcoming')}
          icon={<CalendarBadge />}
          action={
            <button onClick={() => openModal({ patientId: patient.id })} className='p-1 hover:bg-slate-100 rounded text-emerald-600' title={tr('New Appointment')}>
              <Plus size={16} />
            </button>
          }
          items={
            upcomingList.length
              ? upcomingList.map((a) => {
                const start = new Date(a.startTime);
                const end = new Date(a.endTime);
                const date = start.toLocaleDateString([], { dateStyle: 'medium' });
                const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const endTime = end ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
                const timeRange = endTime ? `${startTime} - ${endTime}` : startTime;
                return `${date} • ${timeRange} ${tr('with')} ${a.provider?.name ?? ''}`.trim();
              })
              : patient.upcoming
                ? [patient.upcoming]
                : [tr('No upcoming visits')]
          }
        />
      </div>
      <div className="space-y-2">
        <p className="text-sm font-semibold text-ink">{tr('Timeline')}</p>
        <div className="space-y-2">
          {timelineItems.length === 0 && <div className="text-sm text-slate-500">{tr('No appointment history yet.')}</div>}
          {timelineItems.map((a: any) => (
            <TimelineRow
              key={`${a.id}-${a.startTime}`}
              title={(a.title ? tr(a.title) : '') || (a.type ? tr(a.type) : '') || translateAppointmentTypeName(tr, a.appointmentType?.name)}
              detail={`${new Date(a.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} ${tr('with')} ${a.provider?.name ?? ''
                }`}
              date={translateAppointmentStatus(tr, a.status)}
              onEdit={() => openModal({
                id: a.id,
                patientId: patient.id,
                providerId: a.provider?.id || a.providerId,
                status: a.status,
                appointmentTypeId: a.appointmentType?.id || a.appointmentTypeId,
                start: new Date(a.startTime).toISOString().slice(0, 16),
                durationMinutes: a.durationMinutes || 30, // fallback
                title: a.title,
                isTelemed: a.appointmentType?.isTelemed || false,
              })}
            />
          ))}
        </div>
      </div>
      {visitsByMonth.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-ink">{tr('Visits trend')}</p>
          <div className="border border-slate-100 rounded-lg p-2 bg-white">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
        </div>
      )}
      <ClientOperationsPanel patient={patient} appointmentCount={appointmentInsights.past.length + appointmentInsights.upcoming.length} />
    </div>
  );
}

type ClientRelationship = { id: string; name: string; type: string; phone: string };
type ClientDocument = { id: string; name: string; type: string; updated: string; selected: boolean };
type ClientActivity = {
  id: string;
  title: string;
  description: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
  priority: string;
  dueAt: string | null;
  assigneeName: string;
  updatedAt: string;
};
type GiftVoucher = {
  id: string;
  code: string;
  amount: number;
  balance: number;
  currency: string;
  status: string;
  recipientName: string;
  recipientEmail: string;
  expiresAt: string | null;
  soldAt: string | null;
  emailedAt: string | null;
  redeemedAt: string | null;
  notes: string;
};
type ClientCardProfile = {
  labels: string[];
  relationships: ClientRelationship[];
  accessSettings: Record<string, boolean>;
  communicationPreferences: Record<string, boolean>;
  payors: Array<{ id: string; name: string; responsibility?: string; memberId?: string; groupNumber?: string }>;
  primaryCarePhysician: { name?: string; phone?: string; email?: string };
  salutation: string;
  summaryViews: Array<{ id: string; name: string; sections: string[] }>;
  sidebarFields: Array<{ id: string; label: string; visible: boolean }>;
  pinnedFormIds: string[];
  alerts: Array<{ id: string; label: string; severity?: string }>;
  cases: Array<{ id: string; title: string; status?: string }>;
  authCodes: Array<{ id: string; code: string; description?: string }>;
  storedPaymentCards: Array<{ id: string; label: string }>;
  createMenu: Array<{ id: string; label: string; target: string }>;
  bookingBlocked: boolean;
  bookingBlockReason: string;
  insuranceDefaultPayer: boolean;
  notes: string;
};

function ClientOperationsPanel({ patient, appointmentCount }: { patient: PatientRow; appointmentCount: number }) {
  const { tr } = useI18n();
  const { fetchJson } = useApiClient();
  const [mergeQuery, setMergeQuery] = useState(`${patient.lastName}, ${patient.firstName[0] || ''}`);
  const [relationshipName, setRelationshipName] = useState('');
  const [relationships, setRelationships] = useState<ClientRelationship[]>([
    { id: 'rel-1', name: patient.emergencyContactName || 'Emergency contact', type: 'Emergency contact', phone: patient.emergencyContactPhone || patient.phone },
  ]);
  const [labels, setLabels] = useState<string[]>([]);
  const [labelDraft, setLabelDraft] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(patient.provider || tr('Unassigned'));
  const [payor, setPayor] = useState(patient.insuranceProvider || 'Self pay');
  const [storedCards, setStoredCards] = useState<Array<{ id: string; label: string }>>([]);
  const [shareSent, setShareSent] = useState(false);
  const [summarySections, setSummarySections] = useState(['Clinical alerts', 'Upcoming visits', 'Billing balance']);
  const [referralStatus, setReferralStatus] = useState('Draft');
  const [communicationPreferences, setCommunicationPreferences] = useState<Record<string, boolean>>({ email: Boolean(patient.email), sms: Boolean(patient.phone), marketing: false });
  const [accessSettings, setAccessSettings] = useState<Record<string, boolean>>({ financial: true, clinical: true, documents: true });
  const [pcp, setPcp] = useState({ name: '', phone: '', email: '' });
  const [salutation, setSalutation] = useState(`Dear ${patient.firstName}`);
  const [bookingBlocked, setBookingBlocked] = useState(false);
  const [bookingBlockReason, setBookingBlockReason] = useState('');
  const [insuranceDefaultPayer, setInsuranceDefaultPayer] = useState(Boolean(patient.insuranceProvider));
  const [pinnedFormIds, setPinnedFormIds] = useState<string[]>([]);
  const [alerts, setAlerts] = useState<Array<{ id: string; label: string; severity?: string }>>([]);
  const [cases, setCases] = useState<Array<{ id: string; title: string; status?: string }>>([]);
  const [authCodes, setAuthCodes] = useState<Array<{ id: string; code: string; description?: string }>>([]);
  const [sidebarFields, setSidebarFields] = useState<Array<{ id: string; label: string; visible: boolean }>>([]);
  const [createMenu, setCreateMenu] = useState<Array<{ id: string; label: string; target: string }>>([]);
  const [saveStatus, setSaveStatus] = useState('');
  const [activities, setActivities] = useState<ClientActivity[]>([]);
  const [activityStatus, setActivityStatus] = useState('');
  const [giftVouchers, setGiftVouchers] = useState<GiftVoucher[]>([]);
  const [voucherStatus, setVoucherStatus] = useState('');
  const [voucherLookupCode, setVoucherLookupCode] = useState('');
  const [voucherLookupResult, setVoucherLookupResult] = useState<GiftVoucher | null>(null);
  const [documents, setDocuments] = useState<ClientDocument[]>([
    { id: 'doc-1', name: 'Consent form.pdf', type: 'Form', updated: 'Today', selected: true },
    { id: 'doc-2', name: 'Treatment note.pdf', type: 'Treatment note', updated: 'Yesterday', selected: false },
    { id: 'doc-3', name: 'Insurance card.jpg', type: 'Document', updated: 'Last week', selected: false },
  ]);

  useEffect(() => {
    let active = true;
    fetchJson<ClientCardProfile>(`/patients/${patient.id}/card-profile`)
      .then((profile) => {
        if (!active) return;
        setLabels(profile.labels || []);
        setRelationships(profile.relationships?.length ? profile.relationships : relationships);
        setPayor(profile.payors?.[0]?.name || patient.insuranceProvider || 'Self pay');
        setStoredCards(profile.storedPaymentCards || []);
        setSummarySections(profile.summaryViews?.[0]?.sections || summarySections);
        setCommunicationPreferences(profile.communicationPreferences || communicationPreferences);
        setAccessSettings(profile.accessSettings || accessSettings);
        setPcp({
          name: profile.primaryCarePhysician?.name || '',
          phone: profile.primaryCarePhysician?.phone || '',
          email: profile.primaryCarePhysician?.email || '',
        });
        setSalutation(profile.salutation || `Dear ${patient.firstName}`);
        setBookingBlocked(Boolean(profile.bookingBlocked));
        setBookingBlockReason(profile.bookingBlockReason || '');
        setInsuranceDefaultPayer(Boolean(profile.insuranceDefaultPayer));
        setPinnedFormIds(profile.pinnedFormIds || []);
        setAlerts(profile.alerts || []);
        setCases(profile.cases || []);
        setAuthCodes(profile.authCodes || []);
        setSidebarFields(profile.sidebarFields || []);
        setCreateMenu(profile.createMenu || []);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [fetchJson, patient.id]);

  useEffect(() => {
    let active = true;
    fetchJson<ClientActivity[]>(`/patients/${patient.id}/activities`)
      .then((rows) => {
        if (active) setActivities(rows);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [fetchJson, patient.id]);

  useEffect(() => {
    let active = true;
    fetchJson<GiftVoucher[]>(`/patients/${patient.id}/gift-vouchers`)
      .then((rows) => {
        if (active) setGiftVouchers(rows);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [fetchJson, patient.id]);

  const duplicateCandidates = [
    { id: 'dup-1', name: `${patient.firstName} ${patient.lastName}`, detail: `Same DOB ${patient.dob || '-'} · MRN check` },
    { id: 'dup-2', name: `${patient.firstName} ${patient.lastName[0] || ''}.`, detail: `Same phone ${patient.phone || '-'} · possible portal profile` },
  ].filter((candidate) => candidate.name.toLowerCase().includes(mergeQuery.toLowerCase().replace(',', '').split(' ')[0] || patient.firstName.toLowerCase()));

  function addRelationship() {
    if (!relationshipName.trim()) return;
    setRelationships((current) => [
      ...current,
      { id: `rel-${Date.now()}`, name: relationshipName.trim(), type: 'Family', phone: '' },
    ]);
    setRelationshipName('');
  }

  async function saveProfile(overrides: Partial<ClientCardProfile> = {}) {
    setSaveStatus(tr('Saving...'));
    const payload = {
      labels,
      relationships,
      accessSettings,
      communicationPreferences,
      payors: [{ id: 'primary', name: payor, responsibility: payor === 'Self pay' ? 'Self pay' : 'Insurance' }],
      primaryCarePhysician: pcp,
      salutation,
      summaryViews: [{ id: 'default', name: 'Default summary', sections: summarySections }],
      sidebarFields,
      pinnedFormIds,
      alerts,
      cases,
      authCodes,
      storedPaymentCards: storedCards,
      createMenu,
      bookingBlocked,
      bookingBlockReason,
      insuranceDefaultPayer,
      ...overrides,
    };
    try {
      await fetchJson<ClientCardProfile>(`/patients/${patient.id}/card-profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSaveStatus(tr('Saved'));
      window.setTimeout(() => setSaveStatus(''), 1500);
    } catch (err: any) {
      setSaveStatus(err?.message || tr('Save failed'));
    }
  }

  async function runCardAction(action: string, extra: Record<string, unknown> = {}) {
    const result = await fetchJson<{ secureUrl?: string; printable?: unknown; profile?: ClientCardProfile }>(`/patients/${patient.id}/card-actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action,
        email: patient.email || undefined,
        subject: `${patient.firstName} ${patient.lastName}`,
        message: 'Client card action',
        documentIds: documents.filter((doc) => doc.selected).map((doc) => doc.id),
        attachmentIds: documents.filter((doc) => doc.selected).map((doc) => doc.id),
        formIds: pinnedFormIds,
        ...extra,
      }),
    });
    if (result.profile?.storedPaymentCards) setStoredCards(result.profile.storedPaymentCards);
    if (result.printable) {
      const blob = new Blob([JSON.stringify(result.printable, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const linkEl = document.createElement('a');
      linkEl.href = url;
      linkEl.download = `${patient.mrn || patient.id}-printable-client-export.json`;
      linkEl.click();
      URL.revokeObjectURL(url);
    }
    return result;
  }

  function exportClientData() {
    const payload = {
      patient,
      relationships,
      documents: documents.map(({ selected, ...doc }) => doc),
      summarySections,
      appointmentCount,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const linkEl = document.createElement('a');
    linkEl.href = url;
    linkEl.download = `${patient.mrn || patient.id}-client-export.json`;
    linkEl.click();
    URL.revokeObjectURL(url);
  }

  function toggleSummary(section: string) {
    setSummarySections((current) =>
      current.includes(section) ? current.filter((item) => item !== section) : [...current, section],
    );
  }

  async function reloadActivities() {
    const rows = await fetchJson<ClientActivity[]>(`/patients/${patient.id}/activities`);
    setActivities(rows);
  }

  async function createActivity() {
    const title = window.prompt(tr('Activity title'));
    if (!title?.trim()) return;
    const description = window.prompt(tr('Activity details')) || '';
    setActivityStatus(tr('Saving...'));
    try {
      await fetchJson<ClientActivity>(`/patients/${patient.id}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description, priority: 'Med' }),
      });
      await reloadActivities();
      setActivityStatus(tr('Activity created.'));
    } catch (err: any) {
      setActivityStatus(err?.message || tr('Failed to create activity.'));
    }
  }

  async function editActivity(activity: ClientActivity) {
    const title = window.prompt(tr('Activity title'), activity.title);
    if (!title?.trim()) return;
    const description = window.prompt(tr('Activity details'), activity.description || '') ?? activity.description;
    setActivityStatus(tr('Saving...'));
    try {
      await fetchJson<ClientActivity>(`/patients/${patient.id}/activities/${activity.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description }),
      });
      await reloadActivities();
      setActivityStatus(tr('Activity updated.'));
    } catch (err: any) {
      setActivityStatus(err?.message || tr('Failed to update activity.'));
    }
  }

  async function completeActivity(activity: ClientActivity) {
    setActivityStatus(tr('Saving...'));
    try {
      await fetchJson<ClientActivity>(`/patients/${patient.id}/activities/${activity.id}/complete`, { method: 'POST' });
      await reloadActivities();
      setActivityStatus(tr('Activity completed.'));
    } catch (err: any) {
      setActivityStatus(err?.message || tr('Failed to complete activity.'));
    }
  }

  async function deleteActivity(activity: ClientActivity) {
    if (!window.confirm(`${tr('Delete')} "${activity.title}"?`)) return;
    setActivityStatus(tr('Saving...'));
    try {
      await fetchJson(`/patients/${patient.id}/activities/${activity.id}`, { method: 'DELETE' });
      await reloadActivities();
      setActivityStatus(tr('Activity deleted.'));
    } catch (err: any) {
      setActivityStatus(err?.message || tr('Failed to delete activity.'));
    }
  }

  const formatVoucherMoney = (amount: number, currency = 'USD') =>
    formatMoney(Number(amount || 0), { currency });

  async function reloadGiftVouchers() {
    const rows = await fetchJson<GiftVoucher[]>(`/patients/${patient.id}/gift-vouchers`);
    setGiftVouchers(rows);
  }

  async function createGiftVoucher(mode: 'issue' | 'sell') {
    const amountText = window.prompt(tr('Voucher amount'), '100');
    if (!amountText) return;
    const amount = Number(amountText);
    if (!Number.isFinite(amount) || amount <= 0) {
      setVoucherStatus(tr('Enter a valid voucher amount.'));
      return;
    }
    const durationText = window.prompt(tr('Voucher duration in days'), '365') || '365';
    const recipientName = window.prompt(tr('Recipient name'), `${patient.firstName} ${patient.lastName}`) || '';
    const recipientEmail = window.prompt(tr('Recipient email'), patient.email || '') || '';
    setVoucherStatus(tr('Saving...'));
    try {
      await fetchJson<GiftVoucher>(`/patients/${patient.id}/gift-vouchers/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          durationDays: Number(durationText) || 365,
          recipientName: recipientName || undefined,
          recipientEmail: recipientEmail || undefined,
        }),
      });
      await reloadGiftVouchers();
      setVoucherStatus(mode === 'sell' ? tr('Gift voucher sold.') : tr('Gift voucher issued.'));
    } catch (err: any) {
      setVoucherStatus(err?.message || tr('Unable to save gift voucher.'));
    }
  }

  async function emailGiftVoucher(voucher: GiftVoucher) {
    setVoucherStatus(tr('Saving...'));
    try {
      await fetchJson(`/patients/${patient.id}/gift-vouchers/${voucher.id}/email`, { method: 'POST' });
      await reloadGiftVouchers();
      setVoucherStatus(tr('Gift voucher email recorded.'));
    } catch (err: any) {
      setVoucherStatus(err?.message || tr('Unable to email gift voucher.'));
    }
  }

  async function redeemGiftVoucher(voucher: GiftVoucher) {
    const amountText = window.prompt(tr('Redeem amount'), String(voucher.balance));
    if (!amountText) return;
    const amount = Number(amountText);
    if (!Number.isFinite(amount) || amount <= 0) return;
    setVoucherStatus(tr('Saving...'));
    try {
      await fetchJson(`/patients/${patient.id}/gift-vouchers/${voucher.id}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });
      await reloadGiftVouchers();
      setVoucherStatus(tr('Gift voucher redeemed.'));
    } catch (err: any) {
      setVoucherStatus(err?.message || tr('Unable to redeem gift voucher.'));
    }
  }

  async function changeGiftVoucherDuration(voucher: GiftVoucher) {
    const days = window.prompt(tr('Voucher duration in days'), '365');
    if (!days) return;
    setVoucherStatus(tr('Saving...'));
    try {
      await fetchJson(`/patients/${patient.id}/gift-vouchers/${voucher.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ durationDays: Number(days) || 365 }),
      });
      await reloadGiftVouchers();
      setVoucherStatus(tr('Voucher duration updated.'));
    } catch (err: any) {
      setVoucherStatus(err?.message || tr('Unable to update voucher duration.'));
    }
  }

  async function lookupGiftVoucher() {
    if (!voucherLookupCode.trim()) return;
    setVoucherStatus(tr('Searching...'));
    try {
      const result = await fetchJson<GiftVoucher>(`/patients/${patient.id}/gift-vouchers/lookup?code=${encodeURIComponent(voucherLookupCode.trim())}`);
      setVoucherLookupResult(result);
      setVoucherStatus(tr('Gift voucher found.'));
    } catch (err: any) {
      setVoucherLookupResult(null);
      setVoucherStatus(err?.message || tr('Voucher code not found.'));
    }
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="lg:col-span-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <button className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => void saveProfile()}>
            {tr('Save client card')}
          </button>
          {saveStatus ? <span className="text-xs text-slate-500">{saveStatus}</span> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {createMenu.map((item) => (
            <button key={item.id} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
              {tr(item.label)}
            </button>
          ))}
        </div>
      </div>

      <OperationCard title={tr('Labels, alerts & booking')} icon={<AlertTriangle size={16} className="text-amber-600" />}>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {labels.map((label) => (
              <button key={label} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700" onClick={() => setLabels((current) => current.filter((item) => item !== label))}>
                {label} ×
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={labelDraft} onChange={(event) => setLabelDraft(event.target.value)} placeholder={tr('Add label')} />
            <button className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => {
              if (!labelDraft.trim()) return;
              setLabels((current) => Array.from(new Set([...current, labelDraft.trim()])));
              setLabelDraft('');
            }}>{tr('Add')}</button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bookingBlocked} onChange={(event) => setBookingBlocked(event.target.checked)} className="accent-ink" />
            {tr('Block online booking for this client')}
          </label>
          {bookingBlocked ? (
            <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={bookingBlockReason} onChange={(event) => setBookingBlockReason(event.target.value)} placeholder={tr('Booking block reason')} />
          ) : null}
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => setAlerts((current) => [...current, { id: `alert-${Date.now()}`, label: tr('New client alert'), severity: 'medium' }])}>
            {tr('Create alert')}
          </button>
          {alerts.map((alert) => <p key={alert.id} className="text-xs text-slate-600">{alert.label} · {alert.severity || 'medium'}</p>)}
        </div>
      </OperationCard>

      <OperationCard title={tr('Communication & access')} icon={<Shield size={16} className="text-indigo-600" />}>
        <div className="grid gap-3 sm:grid-cols-2">
          {['email', 'sms', 'marketing'].map((key) => (
            <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <input type="checkbox" checked={Boolean(communicationPreferences[key])} onChange={() => setCommunicationPreferences((current) => ({ ...current, [key]: !current[key] }))} className="accent-ink" />
              {tr(key)}
            </label>
          ))}
          {['clinical', 'documents', 'financial'].map((key) => (
            <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <input type="checkbox" checked={Boolean(accessSettings[key])} onChange={() => setAccessSettings((current) => ({ ...current, [key]: !current[key] }))} className="accent-ink" />
              {tr(`${key} access`)}
            </label>
          ))}
        </div>
        <input className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={salutation} onChange={(event) => setSalutation(event.target.value)} placeholder={tr('Preferred salutation')} />
      </OperationCard>

      <OperationCard title={tr('Duplicate detection & merge')} icon={<Users size={16} className="text-sky-600" />}>
        <div className="space-y-2">
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={mergeQuery}
            onChange={(event) => setMergeQuery(event.target.value)}
            placeholder={tr('Search possible duplicate clients')}
          />
          {duplicateCandidates.map((candidate) => (
            <div key={candidate.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div>
                <p className="font-medium text-ink">{candidate.name}</p>
                <p className="text-xs text-slate-500">{candidate.detail}</p>
              </div>
              <button className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                {tr('Review merge')}
              </button>
            </div>
          ))}
        </div>
      </OperationCard>

      <OperationCard title={tr('Relationships')} icon={<UserPlus size={16} className="text-emerald-600" />}>
        <div className="space-y-2">
          {relationships.map((item) => (
            <div key={item.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <span><strong>{item.name}</strong> · {tr(item.type)} · {item.phone || tr('No phone')}</span>
              <button className="text-slate-400 hover:text-rose-600" onClick={() => setRelationships((current) => current.filter((rel) => rel.id !== item.id))}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={relationshipName} onChange={(event) => setRelationshipName(event.target.value)} placeholder={tr('Add related client')} />
            <button className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={addRelationship}>{tr('Add')}</button>
          </div>
        </div>
      </OperationCard>

      <OperationCard title={tr('Activities')} icon={<ClipboardList size={16} className="text-indigo-600" />}>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-slate-500">{tr('Create, edit, complete, and remove client-card activities.')}</p>
            <button className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => void createActivity()}>
              {tr('Create Activity')}
            </button>
          </div>
          {activityStatus ? <p className="text-xs text-slate-500">{activityStatus}</p> : null}
          {!activities.length ? <p className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-500">{tr('No activities yet.')}</p> : null}
          {activities.slice(0, 8).map((activity) => (
            <div key={activity.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-ink">{activity.title}</p>
                  {activity.description ? <p className="text-xs text-slate-500">{activity.description}</p> : null}
                  <p className="mt-1 text-[11px] text-slate-400">
                    {tr(activity.status.replace(/_/g, ' '))} · {tr('Priority')}: {activity.priority}
                    {activity.dueAt ? ` · ${tr('Due')}: ${new Date(activity.dueAt).toLocaleDateString()}` : ''}
                    {activity.assigneeName ? ` · ${activity.assigneeName}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-1">
                  <button className="rounded border border-slate-200 px-2 py-1 text-xs" onClick={() => void editActivity(activity)}>{tr('Edit')}</button>
                  <button className="rounded border border-emerald-200 px-2 py-1 text-xs text-emerald-700 disabled:opacity-50" disabled={activity.status === 'DONE'} onClick={() => void completeActivity(activity)}>{tr('Complete')}</button>
                  <button className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-600" onClick={() => void deleteActivity(activity)}>{tr('Delete')}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </OperationCard>

      <OperationCard title={tr('Provider & payor')} icon={<Stethoscope size={16} className="text-lime-700" />}>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Preferred provider')}</span>
            <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={selectedProvider} onChange={(event) => setSelectedProvider(event.target.value)}>
              {[patient.provider || tr('Unassigned'), 'Dr. Anna Adams', 'Nurse Mary', 'Any available provider'].map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Payor / insurance')}</span>
            <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={payor} onChange={(event) => setPayor(event.target.value)}>
              {[patient.insuranceProvider || 'Self pay', 'Insurance pending', 'Employer plan', 'Membership plan'].map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Primary care physician')}</span>
            <input className="w-full rounded-lg border border-slate-200 px-3 py-2" value={pcp.name} onChange={(event) => setPcp((current) => ({ ...current, name: event.target.value }))} placeholder={tr('Physician name')} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Insurance default payer')}</span>
            <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={insuranceDefaultPayer ? 'yes' : 'no'} onChange={(event) => setInsuranceDefaultPayer(event.target.value === 'yes')}>
              <option value="yes">{tr('Insurance is default payer')}</option>
              <option value="no">{tr('Do not default insurance')}</option>
            </select>
          </label>
        </div>
        <p className="mt-2 text-xs text-slate-500">{tr('Member ID')}: {patient.insuranceMemberId || '-'} · {tr('Group')}: {patient.insuranceGroupNumber || '-'}</p>
      </OperationCard>

      <OperationCard title={tr('Payments & saved cards')} icon={<CreditCard size={16} className="text-violet-600" />}>
        <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
          <span>{storedCards[0]?.label || tr('No saved card on file')}</span>
          <button className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-600 disabled:opacity-50" disabled={!storedCards.length} onClick={() => void runCardAction('remove_saved_card', { paymentCardId: storedCards[0]?.id })}>
            {tr('Remove saved card')}
          </button>
        </div>
      </OperationCard>

      <OperationCard title={tr('Gift vouchers')} icon={<Gift size={16} className="text-emerald-600" />}>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => void createGiftVoucher('issue')}>
              {tr('Issue Gift Voucher')}
            </button>
            <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void createGiftVoucher('sell')}>
              {tr('Sell Gift Voucher')}
            </button>
          </div>
          <div className="flex gap-2">
            <input
              className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase"
              value={voucherLookupCode}
              onChange={(event) => setVoucherLookupCode(event.target.value)}
              placeholder={tr('Voucher code')}
            />
            <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void lookupGiftVoucher()}>
              {tr('Lookup')}
            </button>
          </div>
          {voucherStatus ? <p className="text-xs text-slate-500">{voucherStatus}</p> : null}
          {voucherLookupResult ? (
            <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              {voucherLookupResult.code} · {tr(voucherLookupResult.status)} · {formatVoucherMoney(voucherLookupResult.balance, voucherLookupResult.currency)} {tr('remaining')}
            </p>
          ) : null}
          {!giftVouchers.length ? <p className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-500">{tr('No gift vouchers yet.')}</p> : null}
          {giftVouchers.slice(0, 6).map((voucher) => (
            <div key={voucher.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-ink">{voucher.code} · {tr(voucher.status)}</p>
                  <p className="text-xs text-slate-500">
                    {formatVoucherMoney(voucher.balance, voucher.currency)} / {formatVoucherMoney(voucher.amount, voucher.currency)}
                    {voucher.expiresAt ? ` · ${tr('Expires')}: ${new Date(voucher.expiresAt).toLocaleDateString()}` : ''}
                  </p>
                  {voucher.recipientEmail ? <p className="text-xs text-slate-400">{voucher.recipientName || tr('Recipient')} · {voucher.recipientEmail}</p> : null}
                </div>
                <div className="flex flex-wrap justify-end gap-1">
                  <button className="rounded border border-slate-200 px-2 py-1 text-xs" onClick={() => void emailGiftVoucher(voucher)}>{tr('Email')}</button>
                  <button className="rounded border border-emerald-200 px-2 py-1 text-xs text-emerald-700 disabled:opacity-50" disabled={voucher.balance <= 0 || ['VOID', 'EXPIRED'].includes(voucher.status)} onClick={() => void redeemGiftVoucher(voucher)}>{tr('Redeem')}</button>
                  <button className="rounded border border-slate-200 px-2 py-1 text-xs" onClick={() => void changeGiftVoucherDuration(voucher)}>{tr('Duration')}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </OperationCard>

      <OperationCard title={tr('Secure sharing')} icon={<Share2 size={16} className="text-cyan-600" />}>
        <div className="grid gap-2 sm:grid-cols-3">
          {['Documents', 'Forms', 'Treatment notes'].map((item) => (
            <label key={item} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <input type="checkbox" defaultChecked className="accent-ink" /> {tr(item)}
            </label>
          ))}
        </div>
        <button className="mt-3 rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => {
          void runCardAction('share_documents');
          setShareSent(true);
        }}>
          {shareSent ? tr('Secure link sent') : tr('Send secure link')}
        </button>
      </OperationCard>

      <OperationCard title={tr('Client data export')} icon={<Download size={16} className="text-amber-600" />}>
        <div className="flex flex-wrap gap-2">
          <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50" onClick={() => void runCardAction('export_printable').catch(exportClientData)}>
            <Download size={14} /> {tr('Export JSON')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50" onClick={() => window.print()}>
            <Printer size={14} /> {tr('Print details')}
          </button>
        </div>
      </OperationCard>

      <OperationCard title={tr('Document browser & email')} icon={<FileText size={16} className="text-blue-600" />}>
        <div className="space-y-2">
          {documents.map((doc) => (
            <label key={doc.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={doc.selected}
                  onChange={() => setDocuments((current) => current.map((item) => item.id === doc.id ? { ...item, selected: !item.selected } : item))}
                  className="accent-ink"
                />
                <span><strong>{doc.name}</strong> · {tr(doc.type)}</span>
              </span>
              <span className="text-xs text-slate-500">{doc.updated}</span>
            </label>
          ))}
          <button className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => void runCardAction('bulk_email_attachments')}>
            <Mail size={14} /> {tr('Email selected attachments')}
          </button>
        </div>
      </OperationCard>

      <OperationCard title={tr('Summary view builder')} icon={<Eye size={16} className="text-teal-600" />}>
        <div className="flex flex-wrap gap-2">
          {['Clinical alerts', 'Upcoming visits', 'Billing balance', 'Documents', 'Relationships', 'Referrals'].map((section) => (
            <button
              key={section}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium',
                summarySections.includes(section) ? 'border-ink bg-ink text-white' : 'border-slate-200 bg-white text-slate-600',
              )}
              onClick={() => toggleSummary(section)}
            >
              {tr(section)}
            </button>
          ))}
        </div>
      </OperationCard>

      <OperationCard title={tr('Referral letters')} icon={<Link size={16} className="text-rose-600" />}>
        <div className="space-y-2">
          <textarea
            className="min-h-[86px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            defaultValue={`Dear colleague,\nPlease review ${patient.firstName} ${patient.lastName} for specialist care.`}
          />
          <div className="flex items-center justify-between">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{tr('Status')}: {tr(referralStatus)}</span>
            <button className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => setReferralStatus('Sent')}>
              {tr('Create referral')}
            </button>
          </div>
        </div>
      </OperationCard>

      <OperationCard title={tr('Forms, cases & authorizations')} icon={<ClipboardList size={16} className="text-purple-600" />}>
        <div className="grid gap-2 sm:grid-cols-2">
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => setPinnedFormIds((current) => Array.from(new Set([...current, `form-${Date.now()}`])))}>
            {tr('Pin form')}
          </button>
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => setCases((current) => [...current, { id: `case-${Date.now()}`, title: tr('Client case'), status: 'Open' }])}>
            {tr('Create case')}
          </button>
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => setAuthCodes((current) => [...current, { id: `auth-${Date.now()}`, code: `AUTH-${current.length + 1}`, description: tr('Authorization code') }])}>
            {tr('Add auth code')}
          </button>
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void runCardAction('password_reset')}>
            {tr('Send password reset')}
          </button>
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void runCardAction('print_labels')}>
            {tr('Print labels')}
          </button>
          <button className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void runCardAction('rotate_photo', { rotation: 90 })}>
            {tr('Rotate photo')}
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {tr('Pinned forms')}: {pinnedFormIds.length} · {tr('Cases')}: {cases.length} · {tr('Auth codes')}: {authCodes.length}
        </p>
      </OperationCard>

      <OperationCard title={tr('Details sidebar')} icon={<Eye size={16} className="text-slate-600" />}>
        <div className="space-y-2">
          {sidebarFields.map((field) => (
            <label key={field.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={field.visible} onChange={() => setSidebarFields((current) => current.map((item) => item.id === field.id ? { ...item, visible: !item.visible } : item))} className="accent-ink" />
              {tr(field.label)}
            </label>
          ))}
        </div>
      </OperationCard>
    </div>
  );
}

function OperationCard({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
      </div>
      {children}
    </section>
  );
}

type PatientLetter = {
  id: string;
  title: string;
  body: string;
  status: string;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  aiDrafted: boolean;
  sharedAt?: string | null;
  secureShareUrl?: string | null;
  updatedAt: string;
};

type EchoAiResponse = {
  title: string;
  contentText: string;
  contentHtml: string;
  checklist: string[];
  mode: 'model' | 'deterministic-fallback';
};

type LetterProvider = {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  userId?: string | null;
  linkedUser?: { id: string; firstName: string; lastName: string } | null;
};

function PatientLettersModule({ patient }: { patient: PatientRow }) {
  const { tr } = useI18n();
  const { fetchJson } = useApiClient();
  const [letters, setLetters] = useState<PatientLetter[]>([]);
  const [providers, setProviders] = useState<LetterProvider[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [title, setTitle] = useState(`Referral letter - ${patient.firstName} ${patient.lastName}`);
  const [body, setBody] = useState('');
  const [assignedToUserId, setAssignedToUserId] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [aiInstruction, setAiInstruction] = useState('Improve clarity and tone for a professional clinical letter.');
  const [letterAiBusy, setLetterAiBusy] = useState<'draft' | 'dictation' | null>(null);
  const [preview, setPreview] = useState<{ title: string; body: string; organization: string; generatedAt: string; printableText?: string } | null>(null);
  const [message, setMessage] = useState('');

  const selectedLetter = letters.find((letter) => letter.id === selectedId) || null;

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchJson<PatientLetter[]>(`/patients/${patient.id}/letters`),
      fetchJson<LetterProvider[]>('/providers').catch(() => [] as LetterProvider[]),
    ])
      .then(([letterRows, providerRows]) => {
        if (!active) return;
        setLetters(letterRows);
        setProviders(providerRows);
        if (letterRows[0]) selectLetter(letterRows[0]);
      })
      .catch((err: any) => {
        if (active) setMessage(err?.message || tr('Unable to load letters.'));
      });
    return () => {
      active = false;
    };
  }, [fetchJson, patient.id]);

  function selectLetter(letter: PatientLetter) {
    setSelectedId(letter.id);
    setTitle(letter.title);
    setBody(letter.body);
    setAssignedToUserId(letter.assignedToUserId || '');
    setStatus(letter.status);
    setPreview(null);
  }

  function resetDraft() {
    setSelectedId('');
    setTitle(`Referral letter - ${patient.firstName} ${patient.lastName}`);
    setBody('');
    setAssignedToUserId('');
    setStatus('DRAFT');
    setPreview(null);
    setMessage('');
  }

  async function saveLetter(aiDraft = false) {
    setMessage(tr('Saving...'));
    const path = selectedId ? `/patients/${patient.id}/letters/${selectedId}` : `/patients/${patient.id}/letters`;
    const saved = await fetchJson<PatientLetter>(path, {
      method: selectedId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title,
        body,
        status,
        assignedToUserId: assignedToUserId || null,
        aiDraft,
      }),
    });
    setLetters((current) => {
      const next = current.some((letter) => letter.id === saved.id)
        ? current.map((letter) => (letter.id === saved.id ? saved : letter))
        : [saved, ...current];
      return next.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    });
    selectLetter(saved);
    setMessage(aiDraft ? tr('AI draft created.') : tr('Letter saved.'));
  }

  async function shareLetter() {
    if (!selectedId) {
      setMessage(tr('Save the letter before sharing it.'));
      return;
    }
    const shared = await fetchJson<PatientLetter>(`/patients/${patient.id}/letters/${selectedId}/share`, { method: 'POST' });
    setLetters((current) => current.map((letter) => (letter.id === shared.id ? shared : letter)));
    selectLetter(shared);
    setMessage(tr('Secure letter link created.'));
  }

  async function previewLetter() {
    if (!selectedId) {
      await saveLetter(false);
      return;
    }
    const nextPreview = await fetchJson<{ title: string; body: string; organization: string; generatedAt: string; printableText?: string }>(
      `/patients/${patient.id}/letters/${selectedId}/preview`,
      { method: 'POST' },
    );
    setPreview(nextPreview);
  }

  async function printLetter() {
    if (!selectedId) {
      setMessage(tr('Save the letter before printing it.'));
      return;
    }
    const printable = await fetchJson<{ title: string; body: string; organization: string; generatedAt: string; printableText?: string }>(
      `/patients/${patient.id}/letters/${selectedId}/print`,
      { method: 'POST' },
    );
    setPreview(printable);
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (printWindow) {
      printWindow.document.write(`<pre style="font-family: Arial, sans-serif; white-space: pre-wrap; line-height: 1.5;">${(printable.printableText || printable.body).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char))}</pre>`);
      printWindow.document.close();
      printWindow.print();
    } else {
      window.print();
    }
  }

  async function echoAssistLetter() {
    if (!selectedId) {
      await saveLetter(false);
      return;
    }
    const assisted = await fetchJson<PatientLetter>(`/patients/${patient.id}/letters/${selectedId}/assist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction: aiInstruction, body }),
    });
    setLetters((current) => current.map((letter) => (letter.id === assisted.id ? assisted : letter)));
    selectLetter(assisted);
    setMessage(tr('Echo AI updated the letter.'));
  }

  async function draftEchoLetter(task: 'letter-generation' | 'letter-dictation', dictatedText?: string) {
    setLetterAiBusy(task === 'letter-dictation' ? 'dictation' : 'draft');
    setMessage(tr('Echo AI is drafting...'));
    try {
      const result = await fetchJson<EchoAiResponse>('/agent/echo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task,
          patientName: `${patient.firstName} ${patient.lastName}`.trim(),
          title,
          sourceText: dictatedText || body,
          instruction: aiInstruction,
        }),
      });
      setBody((current) => [current.trim(), result.contentText.trim()].filter(Boolean).join('\n\n'));
      if (!title.trim() || title.startsWith('Referral letter -')) setTitle(result.title);
      setMessage(tr('Echo AI letter draft inserted.'));
    } catch (err: any) {
      setMessage(err?.message || tr('Echo AI letter draft failed.'));
    } finally {
      setLetterAiBusy(null);
    }
  }

  async function dictateLetter() {
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const dictatedText = !SpeechRecognitionCtor
      ? window.prompt(tr('Paste or type the dictated letter notes'))
      : await new Promise<string>((resolve, reject) => {
          const recognition = new SpeechRecognitionCtor();
          recognition.lang = navigator.language || 'en-US';
          recognition.continuous = false;
          recognition.interimResults = false;
          recognition.onresult = (event: any) => resolve(Array.from(event.results).map((result: any) => result[0]?.transcript || '').join(' '));
          recognition.onerror = () => reject(new Error(tr('Letter dictation failed.')));
          recognition.start();
        }).catch((err: Error) => {
          setMessage(err.message);
          return '';
        });
    if (!dictatedText?.trim()) return;
    await draftEchoLetter('letter-dictation', dictatedText);
  }

  async function deleteLetter() {
    if (!selectedId) {
      setMessage(tr('Select a letter to delete.'));
      return;
    }
    if (!window.confirm(tr('Delete this letter?'))) return;
    await fetchJson(`/patients/${patient.id}/letters/${selectedId}`, { method: 'DELETE' });
    setLetters((current) => current.filter((letter) => letter.id !== selectedId));
    resetDraft();
    setMessage(tr('Letter deleted.'));
  }

  function insertPreviousLetter(previousId: string) {
    const previous = letters.find((letter) => letter.id === previousId);
    if (!previous) return;
    setBody((current) => [current.trim(), previous.body.trim()].filter(Boolean).join('\n\n'));
    setMessage(tr('Previous letter inserted.'));
  }

  function providerLabel(provider: LetterProvider) {
    const linked = provider.linkedUser;
    if (linked) return `${linked.firstName} ${linked.lastName}`;
    return provider.name || `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || tr('Provider');
  }

  function providerUserId(provider: LetterProvider) {
    return provider.linkedUser?.id || provider.userId || provider.id;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <section className="rounded-lg border border-slate-100 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-3">
          <div>
            <p className="text-sm font-semibold text-ink">{tr('Letters')}</p>
            <p className="text-xs text-slate-500">{tr('Secure sharing, assignment, AI drafts, and previews')}</p>
          </div>
          <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium" onClick={resetDraft}>
            {tr('New')}
          </button>
        </div>
        <div className="max-h-[620px] divide-y divide-slate-100 overflow-auto">
          {!letters.length ? <p className="p-3 text-sm text-slate-500">{tr('No letters yet.')}</p> : null}
          {letters.map((letter) => (
            <button
              key={letter.id}
              className={cn('w-full px-3 py-3 text-left hover:bg-slate-50', selectedId === letter.id && 'bg-slate-50')}
              onClick={() => selectLetter(letter)}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-ink">{letter.title}</p>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{tr(letter.status)}</span>
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-slate-500">{letter.body || tr('Draft has no body yet.')}</p>
              <p className="mt-2 text-[11px] text-slate-400">
                {letter.assignedToName ? `${tr('Assigned')}: ${letter.assignedToName} · ` : ''}
                {new Date(letter.updatedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-slate-100 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_160px]">
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Letter title')}</span>
            <input className="w-full rounded-lg border border-slate-200 px-3 py-2" value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Assign to staff')}</span>
            <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={assignedToUserId} onChange={(event) => setAssignedToUserId(event.target.value)}>
              <option value="">{tr('Unassigned')}</option>
              {providers.map((provider) => (
                <option key={provider.id} value={providerUserId(provider)}>{providerLabel(provider)}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-500">{tr('Status')}</span>
            <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value)}>
              {['DRAFT', 'FINAL', 'SHARED', 'ARCHIVED'].map((option) => <option key={option} value={option}>{tr(option)}</option>)}
            </select>
          </label>
        </div>

        <textarea
          className="min-h-[300px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm leading-6"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={tr('Write or generate the letter body.')}
        />

        <div className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1fr_auto]">
          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={aiInstruction}
            onChange={(event) => setAiInstruction(event.target.value)}
            placeholder={tr('Echo AI instruction')}
          />
          <button className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium" onClick={() => void echoAssistLetter()}>
            <Pencil size={14} /> {tr('Echo AI assist')}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800 disabled:opacity-50" disabled={Boolean(letterAiBusy)} onClick={() => void draftEchoLetter('letter-generation')}>
            {letterAiBusy === 'draft' ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} {tr('AI Letter Generation')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-sky-800 disabled:opacity-50" disabled={Boolean(letterAiBusy)} onClick={() => void dictateLetter()}>
            {letterAiBusy === 'dictation' ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />} {tr('AI Letter Dictation')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white" onClick={() => void saveLetter(false)}>
            <FileText size={14} /> {tr('Save letter')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void saveLetter(true)}>
            <Pencil size={14} /> {tr('AI draft')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void shareLetter()}>
            <Share2 size={14} /> {tr('Share securely')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void previewLetter()}>
            <Eye size={14} /> {tr('Preview')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium" onClick={() => void printLetter()}>
            <Printer size={14} /> {tr('Print')}
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-600" onClick={() => void deleteLetter()}>
            <Trash2 size={14} /> {tr('Delete')}
          </button>
          <select className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value="" onChange={(event) => insertPreviousLetter(event.target.value)}>
            <option value="">{tr('Insert previous letter')}</option>
            {letters.filter((letter) => letter.id !== selectedId).map((letter) => (
              <option key={letter.id} value={letter.id}>{letter.title}</option>
            ))}
          </select>
          {message ? <span className="text-xs text-slate-500">{message}</span> : null}
        </div>

        {selectedLetter?.secureShareUrl ? (
          <div className="rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2 text-sm text-cyan-800">
            {tr('Secure share URL')}: {selectedLetter.secureShareUrl}
          </div>
        ) : null}

        {preview ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 border-b border-slate-200 pb-3">
              <p className="text-xs uppercase text-slate-500">{preview.organization}</p>
              <h3 className="text-lg font-semibold text-ink">{preview.title}</h3>
              <p className="text-xs text-slate-500">{new Date(preview.generatedAt).toLocaleString()}</p>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm leading-6 text-slate-700">{preview.body}</pre>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ClinicalTab({ patient }: { patient: PatientRow }) {
  const { tr } = useI18n();

  return (
    <div className="space-y-3 text-sm text-slate-600">
      <Section title={tr('Problems')}>{patient.problems.length ? patient.problems.join(', ') : tr('No active problems')}</Section>
      <Section title={tr('Allergies')}>{patient.allergies.length ? patient.allergies.join(', ') : tr('No known allergies')}</Section>
      <Section title={tr('Histories')}>
        <ul className="list-disc list-inside space-y-1">
          <li>{tr('Past medical: Hypertension (since 2016)')}</li>
          <li>{tr('Family: Father - HTN, Mother - T2DM')}</li>
          <li>{tr('Social: Non-smoker, occasional alcohol')}</li>
        </ul>
      </Section>
      <Section title={tr('Vitals trend')}>
        <p>{tr('BP 128/78 | HR 72 | SpO2 98% (stable)')}</p>
      </Section>
    </div>
  );
}

function MedicationsTab({ patient }: { patient: PatientRow }) {
  const { tr } = useI18n();

  return (
    <div className="space-y-2 text-sm">
      {patient.medications.map((m) => (
        <div key={m} className="p-3 rounded-lg border border-slate-100 bg-slate-50 flex items-center gap-2">
          <Pill size={16} className="text-emerald-600" /> {m}
        </div>
      ))}
      {!patient.medications.length && <p className="text-slate-500">{tr('No active medications.')}</p>}
    </div>
  );
}

function toLocalDateTimeInput(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

type ServiceTimelineEvent = {
  id: string;
  type: string;
  occurredAt: string;
  title: string;
  status?: string;
  location?: { name: string } | null;
  provider?: { name: string } | null;
  notes?: string | null;
  paymentStatus?: string;
  stockSummary?: Array<{ itemName?: string; actualQuantity?: number; unit?: string; brand?: string; lotNumber?: string }>;
  financial?: Record<string, number | string | null> | null;
};

function ServiceTimelineTab({ patientId }: { patientId: string }) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { tr } = useI18n();
  const [events, setEvents] = useState<ServiceTimelineEvent[]>([]);
  const [canViewFinancial, setCanViewFinancial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    (async () => {
      try {
        let token = '';
        try {
          token = await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
        } catch (err: any) {
          if (err?.error === 'login_required') {
            await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
            return;
          }
          throw err;
        }
        const response = await fetch(`${API_BASE}/patients/${patientId}/service-timeline`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!response.ok) throw new Error(await response.text());
        const payload = await response.json();
        if (!active) return;
        setEvents(payload.data || []);
        setCanViewFinancial(Boolean(payload.canViewFinancial));
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load service timeline.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getAccessTokenSilently, loginWithRedirect, patientId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Elenthor')}</p>
        <h4 className="text-base font-semibold text-slate-900">{tr('Service timeline')}</h4>
        {!canViewFinancial ? <p className="mt-1 text-xs text-slate-500">{tr('Financial amounts are hidden for this account.')}</p> : null}
      </div>
      {error ? <div className="m-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
      {loading ? (
        <div className="p-4 text-sm text-slate-500">{tr('Loading...')}</div>
      ) : events.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">{tr('No service history yet.')}</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {events.map((event) => (
            <div key={event.id} className="grid gap-2 px-4 py-3 text-sm md:grid-cols-[170px_1fr_180px]">
              <div className="text-slate-500">{new Date(event.occurredAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{tr(event.title)}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{event.type.replace(/_/g, ' ')}</span>
                  {event.status ? <span className="text-xs text-slate-500">{translateAppointmentStatus(tr, event.status)}</span> : null}
                </div>
                <p className="text-xs text-slate-500">
                  {[event.location?.name, event.provider?.name].filter(Boolean).join(' · ')}
                </p>
                {event.notes ? <p className="mt-1 text-slate-600">{event.notes}</p> : null}
                {event.stockSummary?.length ? (
                  <p className="mt-1 text-xs text-slate-500">
                    {event.stockSummary.map((item) => `${item.itemName || 'Item'} ${item.actualQuantity ?? ''}${item.unit || ''}${item.lotNumber ? ` lot ${item.lotNumber}` : ''}`).join(', ')}
                  </p>
                ) : null}
              </div>
              <div className="text-xs text-slate-600">
                {event.paymentStatus ? <p>{tr('Payment')}: {event.paymentStatus.replace(/_/g, ' ')}</p> : null}
                {canViewFinancial && event.financial ? (
                  <div className="mt-1 space-y-0.5">
                    {Object.entries(event.financial).map(([key, value]) => (
                      value !== null && value !== undefined ? <p key={key}>{key}: {String(value)}</p> : null
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type PatientVisitHistoryItem = {
  id: string;
  status: string;
  appointmentId?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
  visitReason?: string | null;
  summaryText?: string | null;
  provider?: { id: string; name: string } | null;
  appointment?: {
    appointmentType?: { id: string; name: string } | null;
    billingService?: { id: string; name: string } | null;
    location?: { id: string; name: string } | null;
    paymentStatus?: string | null;
    financial?: { total?: number | null; balance?: number | null; invoiceNumber?: string | null } | null;
  } | null;
  treatmentCompletions?: Array<{ id: string; performedServiceName: string; paymentStatus?: string | null; completedAt?: string | null }>;
};

function PatientVisitsTab({ patientId }: { patientId: string }) {
  const { tr } = useI18n();
  const navigate = useNavigate();
  const { fetchJson } = useApiClient();
  const [visits, setVisits] = useState<PatientVisitHistoryItem[]>([]);
  const [canViewFinancial, setCanViewFinancial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchJson<{ canViewFinancial: boolean; data: PatientVisitHistoryItem[] }>(`/patients/${patientId}/visits?pageSize=50`)
      .then((payload) => {
        if (!active) return;
        setVisits(payload.data || []);
        setCanViewFinancial(Boolean(payload.canViewFinancial));
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load visits.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchJson, patientId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Visits')}</p>
        <h4 className="text-base font-semibold text-slate-900">{tr('Visit History')}</h4>
      </div>
      {error ? <div className="m-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
      {loading ? (
        <div className="p-4 text-sm text-slate-500">{tr('Loading...')}</div>
      ) : visits.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">{tr('No visits recorded.')}</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {visits.map((visit) => {
            const occurredAt = visit.completedAt || visit.startedAt || visit.updatedAt;
            const performedServiceName = visit.treatmentCompletions?.[0]?.performedServiceName || '';
            const bookedServiceName = visit.appointment?.billingService?.name || '';
            const sessionTypeName = visit.appointment?.appointmentType?.name || '';
            const serviceName = performedServiceName || bookedServiceName || sessionTypeName || 'Visit';
            const financial = canViewFinancial ? visit.appointment?.financial : null;
            return (
              <button
                key={visit.id}
                type="button"
                onClick={() => navigate(`/visits/${visit.id}`)}
                className="grid w-full gap-3 px-4 py-3 text-left text-sm hover:bg-slate-50 md:grid-cols-[170px_1fr_170px]"
              >
                <div className="text-slate-500">{occurredAt ? new Date(occurredAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '-'}</div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{tr(serviceName)}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{tr(visit.status.replace(/_/g, ' '))}</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {[
                      performedServiceName && sessionTypeName && performedServiceName !== sessionTypeName ? `${tr('Appointment type')}: ${tr(sessionTypeName)}` : '',
                      !performedServiceName && bookedServiceName && sessionTypeName && bookedServiceName !== sessionTypeName ? `${tr('Appointment type')}: ${tr(sessionTypeName)}` : '',
                      visit.provider?.name,
                      visit.appointment?.location?.name,
                    ].filter(Boolean).join(' · ')}
                  </p>
                  {visit.visitReason ? <p className="mt-1 text-slate-700">{visit.visitReason}</p> : null}
                  {visit.summaryText ? <p className="mt-1 text-slate-600 line-clamp-2">{visit.summaryText}</p> : null}
                </div>
                <div className="text-xs text-slate-500 md:text-right">
                  {visit.treatmentCompletions?.length ? <p>{tr('Treatment')}: {tr(visit.treatmentCompletions[0].performedServiceName)}</p> : null}
                  {!visit.treatmentCompletions?.length && bookedServiceName ? <p>{tr('Booked')}: {tr(bookedServiceName)}</p> : null}
                  {visit.appointment?.paymentStatus ? <p>{tr('Payment')}: {tr(visit.appointment.paymentStatus.replace(/_/g, ' '))}</p> : null}
                  {financial ? <p>{tr('Balance')}: {Number(financial.balance ?? 0).toFixed(2)}</p> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppointmentsTab({
  appointments = [],
  patientId,
}: {
  appointments?: DetailProps['appointments'];
  patientId: string;
}) {
  const { openModal } = useAppointmentModalStore();
  const { tr } = useI18n();
  const navigate = useNavigate();
  const { fetchJson } = useApiClient();
  const [busyVisitId, setBusyVisitId] = useState<string | null>(null);
  const [rows, setRows] = useState<any[]>(appointments || []);
  const [busyAppointmentId, setBusyAppointmentId] = useState<string | null>(null);
  const [historyAppointmentId, setHistoryAppointmentId] = useState<string | null>(null);
  const [history, setHistory] = useState<{
    rescheduled: boolean;
    rebookedAppointments: Array<{ id: string; startTime: string; status: string }>;
    audit: Array<{ id: string; action: string; createdAt: string; userName?: string | null; metadata?: Record<string, unknown> }>;
  } | null>(null);

  useEffect(() => {
    setRows(appointments || []);
  }, [appointments]);

  const sortedAppointments = [...rows].sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );

  function openCreateAppointment() {
    openModal({
      patientId,
      start: toLocalDateTimeInput(new Date()),
      durationMinutes: 30,
      status: 'SCHEDULED',
    });
  }

  function openEditAppointment(appt: any) {
    const startMs = new Date(appt.startTime).getTime();
    const endMs = new Date(appt.endTime).getTime();
    const durationMinutes = Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
      ? Math.max(1, Math.round((endMs - startMs) / 60000))
      : appt.durationMinutes || appt.appointmentType?.durationMinutes || 30;

    openModal({
      id: appt.id,
      patientId: appt.patientId || appt.patient?.id || patientId,
      providerId: appt.provider?.id || appt.providerId,
      status: appt.status,
      appointmentTypeId: appt.appointmentType?.id || appt.appointmentTypeId,
      start: toLocalDateTimeInput(new Date(appt.startTime)),
      durationMinutes,
      title: appt.title,
      isTelemed: appt.appointmentType?.isTelemed || false,
    });
  }

  async function startOrOpenVisit(appt: any, event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (appt.encounterId) {
      navigate(`/visits/${appt.encounterId}`);
      return;
    }
    setBusyVisitId(appt.id);
    try {
      const result = await fetchJson<{ encounterId: string }>('/encounters/start-from-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId: appt.id }),
      });
      navigate(`/visits/${result.encounterId}`);
    } finally {
      setBusyVisitId(null);
    }
  }

  async function runAppointmentAction(appt: any, action: 'cancel' | 'reinstate' | 'resend-confirmation' | 'rebook' | 'delete', event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (action === 'delete' && !window.confirm(tr('Delete this appointment?'))) return;
    if (action === 'cancel' && !window.confirm(tr('Cancel this appointment?'))) return;
    setBusyAppointmentId(appt.id);
    try {
      if (action === 'delete') {
        await fetchJson(`/appointments/${appt.id}`, { method: 'DELETE' });
        setRows((current) => current.filter((item) => item.id !== appt.id));
        return;
      }
      const result = await fetchJson<{ id?: string }>(`/appointments/${appt.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'rebook' ? { note: 'Rebooked from client card' } : {}),
      });
      if (action === 'cancel') {
        setRows((current) => current.map((item) => item.id === appt.id ? { ...item, status: 'CANCELLED' } : item));
      } else if (action === 'reinstate') {
        setRows((current) => current.map((item) => item.id === appt.id ? { ...item, status: 'SCHEDULED' } : item));
      } else if (action === 'resend-confirmation') {
        setRows((current) => current.map((item) => item.id === appt.id ? { ...item, confirmedAt: item.confirmedAt || new Date().toISOString() } : item));
      } else if (action === 'rebook' && result.id) {
        setRows((current) => [
          {
            ...appt,
            id: result.id,
            status: 'SCHEDULED',
            startTime: new Date(new Date(appt.startTime).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            endTime: new Date(new Date(appt.endTime).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            rescheduledFromAppointmentId: appt.id,
            confirmedAt: null,
          },
          ...current,
        ]);
      }
    } finally {
      setBusyAppointmentId(null);
    }
  }

  async function loadAppointmentHistory(appt: any, event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setHistoryAppointmentId(appt.id);
    setHistory(null);
    const payload = await fetchJson<typeof history>(`/appointments/${appt.id}/history`);
    setHistory(payload);
  }

  async function editAppointmentNotes(appt: any, event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    const nextNotes = window.prompt(tr('Appointment Notes'), appt.notes || '');
    if (nextNotes === null || nextNotes === (appt.notes || '')) return;
    setBusyAppointmentId(appt.id);
    try {
      await fetchJson(`/appointments/${appt.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: nextNotes }),
      });
      setRows((current) => current.map((item) => (item.id === appt.id ? { ...item, notes: nextNotes } : item)));
    } finally {
      setBusyAppointmentId(null);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Appointments')}</p>
          <h4 className="text-base font-semibold text-slate-900">{tr('Patient Appointments')}</h4>
        </div>
        <button className="inline-flex items-center gap-2 rounded-xl bg-lime-400 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-lime-300" onClick={openCreateAppointment}>
          <Plus size={14} />
          {tr('New Appointment')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
              <th className="px-4 py-3 text-left font-medium">{tr('Date')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Title / Type')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Provider')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Status')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Duration')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Tracking')}</th>
              <th className="px-4 py-3 text-right font-medium">{tr('Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedAppointments.map((appt: any) => {
              const serviceDisplay = getAppointmentDisplayNames(appt);
              const classMeta = patientAppointmentClassMeta(appt);
              const start = new Date(appt.startTime);
              const end = new Date(appt.endTime);
              const durationMinutes = Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && end.getTime() > start.getTime()
                ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000))
                : appt.durationMinutes || appt.appointmentType?.durationMinutes || 30;
              return (
                <tr key={appt.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openEditAppointment(appt)}>
                  <td className="px-4 py-3 text-slate-600">{start.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                  <td className="px-4 py-3 text-slate-900">
                    <p className="font-medium">
                      {(appt.title ? tr(appt.title) : '') || (appt.type ? tr(appt.type) : '') || tr(serviceDisplay.primaryName)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {serviceDisplay.secondaryName ? `${tr('Appointment type')}: ${translateAppointmentTypeName(tr, serviceDisplay.secondaryName)}` : '-'}
                    </p>
                    {classMeta.isClass ? <p className="mt-1 text-xs font-semibold text-emerald-700">{tr('Class booking')}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{appt.provider?.name || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{translateAppointmentStatus(tr, appt.status)}</td>
                  <td className="px-4 py-3 text-slate-700">{durationMinutes} {tr('min')}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="space-y-1 text-xs">
                      <p>{appt.confirmedAt ? tr('Confirmed') : tr('Awaiting confirmation')}</p>
                      <p>{appt.rescheduledFromAppointmentId ? tr('Rebooked / rescheduled') : tr('Original booking')}</p>
                      <p>{appt.notes ? tr('Has appointment notes') : tr('No appointment notes')}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={(event) => startOrOpenVisit(appt, event)}
                        disabled={busyVisitId === appt.id || ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(appt.status)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Stethoscope size={13} />
                        {tr(appt.encounterId ? 'Open' : 'Start')}
                      </button>
                      <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium" disabled={busyAppointmentId === appt.id} onClick={(event) => runAppointmentAction(appt, 'resend-confirmation', event)}>
                        {tr('Resend')}
                      </button>
                      {appt.status === 'CANCELLED' ? (
                        <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium" disabled={busyAppointmentId === appt.id} onClick={(event) => runAppointmentAction(appt, 'reinstate', event)}>
                          {tr('Reinstate')}
                        </button>
                      ) : (
                        <button type="button" className="rounded-lg border border-amber-200 px-2.5 py-1.5 text-xs font-medium text-amber-700" disabled={busyAppointmentId === appt.id} onClick={(event) => runAppointmentAction(appt, 'cancel', event)}>
                          {tr(classMeta.isClass ? 'Cancel class booking' : 'Cancel')}
                        </button>
                      )}
                      {classMeta.isClass ? (
                        <button type="button" className="rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-medium text-emerald-700" onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/appointments/${appt.id}/payment?focus=class`);
                        }}>
                          {tr('Class Payments')}
                        </button>
                      ) : null}
                      <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium" disabled={busyAppointmentId === appt.id} onClick={(event) => runAppointmentAction(appt, 'rebook', event)}>
                        {tr('Rebook')}
                      </button>
                      <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium" onClick={(event) => loadAppointmentHistory(appt, event)}>
                        {tr('Audit')}
                      </button>
                      <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium" disabled={busyAppointmentId === appt.id} onClick={(event) => editAppointmentNotes(appt, event)}>
                        {tr('Notes')}
                      </button>
                      <button type="button" className="rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600" disabled={busyAppointmentId === appt.id} onClick={(event) => runAppointmentAction(appt, 'delete', event)}>
                        {tr('Delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!sortedAppointments.length && (
              <tr>
                <td className="px-4 py-6 text-center text-slate-500" colSpan={7}>
                  {tr('No appointments recorded.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {historyAppointmentId ? (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">{tr('Appointment Audit Log')}</p>
              <p className="text-xs text-slate-500">{history?.rescheduled ? tr('Reschedule tracking found related appointment changes.') : tr('No reschedule chain found.')}</p>
            </div>
            <button className="text-xs text-slate-500 hover:text-ink" onClick={() => {
              setHistoryAppointmentId(null);
              setHistory(null);
            }}>{tr('Close')}</button>
          </div>
          {!history ? <p className="text-sm text-slate-500">{tr('Loading...')}</p> : null}
          {history?.rebookedAppointments?.length ? (
            <div className="mb-2 rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-600">
              {history.rebookedAppointments.map((item) => (
                <p key={item.id}>{tr('Rebooked to')} {new Date(item.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} · {tr(item.status)}</p>
              ))}
            </div>
          ) : null}
          <div className="space-y-1">
            {history?.audit.map((entry) => (
              <div key={entry.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                <span className="font-semibold text-slate-900">{tr(entry.action)}</span>
                {' · '}
                {new Date(entry.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                {entry.userName ? ` · ${entry.userName}` : ''}
              </div>
            ))}
            {history && !history.audit.length ? <p className="text-sm text-slate-500">{tr('No audit entries yet.')}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BillingTab() {
  return (
    <div className="space-y-2 text-sm">
      <div className="p-3 rounded-lg border border-slate-100 flex items-center justify-between">
        <div>
          <p className="font-semibold text-ink">Encounter #8832</p>
          <p className="text-slate-500">Amount: $120.00 - Paid</p>
        </div>
        <span className="pill pill-success">Paid</span>
      </div>
      <div className="p-3 rounded-lg border border-slate-100 flex items-center justify-between">
        <div>
          <p className="font-semibold text-ink">Encounter #8721</p>
          <p className="text-slate-500">Amount: $80.00 - Pending</p>
        </div>
        <span className="pill pill-warn">Pending</span>
      </div>
    </div>
  );
}

function isTelemedAppointment(appt: any) {
  if (appt?.appointmentType?.isTelemed) return true;
  if (appt?.isTelemed) return true;
  return false;
}

function TelemedTab({
  appointments = [],
  patientId,
}: {
  appointments?: DetailProps['appointments'];
  patientId: string;
}) {
  const { openModal } = useAppointmentModalStore();
  const { tr } = useI18n();
  const televisits = [...appointments]
    .filter((appt) => isTelemedAppointment(appt))
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  function openCreateTelevisit() {
    const fallbackTelemedTypeId = televisits.find((appt: any) => appt.appointmentType?.isTelemed && appt.appointmentType?.id)?.appointmentType?.id;
    openModal({
      patientId,
      start: toLocalDateTimeInput(new Date()),
      durationMinutes: 30,
      status: 'SCHEDULED',
      isTelemed: true,
      generateTelemedLink: true,
      appointmentTypeId: fallbackTelemedTypeId,
    });
  }

  function openEditTelevisit(appt: any) {
    const startMs = new Date(appt.startTime).getTime();
    const endMs = new Date(appt.endTime).getTime();
    const durationMinutes = Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
      ? Math.max(1, Math.round((endMs - startMs) / 60000))
      : appt.durationMinutes || appt.appointmentType?.durationMinutes || 30;

    openModal({
      id: appt.id,
      patientId: appt.patientId || appt.patient?.id || patientId,
      providerId: appt.provider?.id || appt.providerId,
      status: appt.status,
      appointmentTypeId: appt.appointmentType?.id || appt.appointmentTypeId,
      start: toLocalDateTimeInput(new Date(appt.startTime)),
      durationMinutes,
      title: appt.title,
      isTelemed: true,
      generateTelemedLink: true,
      joinUrl: appt.joinUrl || null,
    });
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Telemedicine')}</p>
          <h4 className="text-base font-semibold text-slate-900">{tr('Patient Televisits')}</h4>
        </div>
        <button className="inline-flex items-center gap-2 rounded-xl bg-lime-400 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-lime-300" onClick={openCreateTelevisit}>
          <Plus size={14} />
          {tr('New Televisit')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
              <th className="px-4 py-3 text-left font-medium">{tr('Date')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Title / Type')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Provider')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Status')}</th>
              <th className="px-4 py-3 text-left font-medium">{tr('Duration')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {televisits.map((appt: any) => {
              const serviceDisplay = getAppointmentDisplayNames(appt);
              const start = new Date(appt.startTime);
              const end = new Date(appt.endTime);
              const durationMinutes = Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && end.getTime() > start.getTime()
                ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000))
                : appt.durationMinutes || appt.appointmentType?.durationMinutes || 30;
              return (
                <tr key={appt.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openEditTelevisit(appt)}>
                  <td className="px-4 py-3 text-slate-600">{start.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                  <td className="px-4 py-3 text-slate-900">
                    <p className="font-medium">
                      {(appt.title ? tr(appt.title) : '') || (appt.type ? tr(appt.type) : '') || tr(serviceDisplay.primaryName) || tr('Televisit')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {serviceDisplay.secondaryName ? `${tr('Appointment type')}: ${translateAppointmentTypeName(tr, serviceDisplay.secondaryName)}` : tr('Telemedicine')}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{appt.provider?.name || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{translateAppointmentStatus(tr, appt.status)}</td>
                  <td className="px-4 py-3 text-slate-700">{durationMinutes} {tr('min')}</td>
                </tr>
              );
            })}
            {!televisits.length && (
              <tr>
                <td className="px-4 py-6 text-center text-slate-500" colSpan={5}>
                  {tr('No telemedicine appointments recorded.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type EditPatientModalProps = {
  open: boolean;
  patient: PatientRow;
  onClose: () => void;
  onSaved: (p: PatientRow) => void;
};

function EditPatientModal({ open, patient, onClose, onSaved }: EditPatientModalProps) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { tr } = useI18n();
  const [form, setForm] = useState(() => ({
    firstName: patient.firstName,
    middleName: patient.middleName ?? '',
    lastName: patient.lastName,
    dob: patient.dob ? patient.dob.slice(0, 10) : '',
    sex: patient.sex,
    genderIdentity: patient.genderIdentity ?? '',
    mrn: patient.mrn,
    phone: patient.phone ?? '',
    email: patient.email ?? '',
    addressLine1: patient.addressLine1 ?? '',
    addressLine2: patient.addressLine2 ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    postalCode: patient.postalCode ?? '',
    country: patient.country ?? 'US',
    formattedAddress: patient.formattedAddress ?? '',
    googlePlaceId: patient.googlePlaceId ?? '',
    latitude: patient.latitude != null ? String(patient.latitude) : '',
    longitude: patient.longitude != null ? String(patient.longitude) : '',
    source: patient.source ?? 'MANUAL',
    insuranceProvider: patient.insuranceProvider ?? '',
    insuranceMemberId: patient.insuranceMemberId ?? '',
    insuranceGroupNumber: patient.insuranceGroupNumber ?? '',
    emergencyName: patient.emergencyContactName ?? '',
    emergencyPhone: patient.emergencyContactPhone ?? '',
  }));
  const [avatarUrl, setAvatarUrl] = useState(patient.avatarUrl ?? '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [allergies, setAllergies] = useState<AllergyEntry[]>(patient.allergiesRaw);
  const [problems, setProblems] = useState<ProblemEntry[]>(patient.problemsRaw);
  const [medications, setMedications] = useState<MedicationEntry[]>(patient.medicationsRaw);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [medQuery, setMedQuery] = useState('');
  const [medSuggestions, setMedSuggestions] = useState<string[]>([]);
  const medFallback = [
    'Lisinopril 10mg',
    'Metformin 500mg',
    'Atorvastatin 20mg',
    'Amlodipine 5mg',
    'Albuterol',
    'Levothyroxine',
    'Omeprazole',
    'Gabapentin',
  ];

  useEffect(() => {
    setForm({
      firstName: patient.firstName,
      middleName: patient.middleName ?? '',
      lastName: patient.lastName,
      dob: patient.dob ? patient.dob.slice(0, 10) : '',
      sex: patient.sex,
      genderIdentity: patient.genderIdentity ?? '',
      mrn: patient.mrn,
      phone: patient.phone ?? '',
      email: patient.email ?? '',
      addressLine1: patient.addressLine1 ?? '',
      addressLine2: patient.addressLine2 ?? '',
      city: patient.city ?? '',
      state: patient.state ?? '',
      postalCode: patient.postalCode ?? '',
      country: patient.country ?? 'US',
      formattedAddress: patient.formattedAddress ?? '',
      googlePlaceId: patient.googlePlaceId ?? '',
      latitude: patient.latitude != null ? String(patient.latitude) : '',
      longitude: patient.longitude != null ? String(patient.longitude) : '',
      source: patient.source ?? 'MANUAL',
      insuranceProvider: patient.insuranceProvider ?? '',
      insuranceMemberId: patient.insuranceMemberId ?? '',
      insuranceGroupNumber: patient.insuranceGroupNumber ?? '',
      emergencyName: patient.emergencyContactName ?? '',
      emergencyPhone: patient.emergencyContactPhone ?? '',
    });
    setAvatarUrl(patient.avatarUrl ?? '');
    setAvatarUploadError(null);
    setAllergies(patient.allergiesRaw);
    setProblems(patient.problemsRaw);
    setMedications(patient.medicationsRaw);
  }, [patient]);

  useEffect(() => {
    if (!medQuery.trim()) {
      setMedSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://clinicaltables.nlm.nih.gov/api/drugnames/v3/search?q=${encodeURIComponent(medQuery)}&maxList=12`,
          { signal: controller.signal },
        );
        if (res.ok) {
          const data = await res.json();
          setMedSuggestions(data?.[1] ?? medFallback.filter((m) => m.toLowerCase().includes(medQuery.toLowerCase())));
        } else {
          setMedSuggestions(medFallback.filter((m) => m.toLowerCase().includes(medQuery.toLowerCase())));
        }
      } catch {
        if (!controller.signal.aborted) {
          setMedSuggestions(medFallback.filter((m) => m.toLowerCase().includes(medQuery.toLowerCase())));
        }
      }
    }, 200);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [medQuery]);

  if (!open) return null;

  async function uploadAvatar(file?: File) {
    if (!file) return;
    setAvatarUploadError(null);
    setError(null);
    if (!AVATAR_ALLOWED_TYPES.includes(file.type as (typeof AVATAR_ALLOWED_TYPES)[number])) {
      setAvatarUploadError('Avatar must be a PNG, JPEG, or WEBP image.');
      return;
    }
    if (file.size > AVATAR_MAX_SOURCE_BYTES) {
      setAvatarUploadError('Avatar file must be 5MB or smaller.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const optimizedFile = await optimizeAvatarFile(file);
      let token = '';
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH_AUDIENCE },
        });
      } catch (err: any) {
        if (err?.error === 'login_required') {
          await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
          return;
        }
        throw err;
      }

      const presignRes = await fetch(`${API_BASE}/storage/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          fileName: optimizedFile.name || 'avatar.jpg',
          contentType: optimizedFile.type || 'image/jpeg',
          kind: 'patient-avatar',
          patientId: patient.id,
        }),
      });
      if (!presignRes.ok) {
        throw new Error(tr('Unable to prepare avatar upload'));
      }

      const presign = (await presignRes.json()) as { uploadUrl: string; url: string };
      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': optimizedFile.type || 'image/jpeg',
        },
        body: optimizedFile,
      });
      if (!uploadRes.ok) {
        throw new Error(tr('Avatar upload failed'));
      }

      setAvatarUrl(presign.url);
    } catch (err: any) {
      setAvatarUploadError(err?.message || tr('Avatar upload failed'));
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      let token = '';
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH_AUDIENCE },
        });
      } catch (err: any) {
        if (err?.error === 'login_required') {
          await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
          return;
        }
        throw err;
      }
      const payload = {
        mrn: form.mrn,
        firstName: form.firstName,
        middleName: form.middleName || undefined,
        lastName: form.lastName,
        avatarUrl: avatarUrl || undefined,
        dob: form.dob || patient.dob,
        sex: form.sex,
        genderIdentity: form.genderIdentity || undefined,
        phone: form.phone || undefined,
        phoneSecondary: patient.phoneSecondary || undefined,
        email: form.email || undefined,
        status: patient.status,
        addressLine1: form.addressLine1 || undefined,
        addressLine2: form.addressLine2 || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country || undefined,
        formattedAddress: form.formattedAddress || undefined,
        googlePlaceId: form.googlePlaceId || undefined,
        latitude: form.latitude ? Number(form.latitude) : undefined,
        longitude: form.longitude ? Number(form.longitude) : undefined,
        source: form.source || undefined,
        insuranceProvider: form.insuranceProvider || undefined,
        insuranceMemberId: form.insuranceMemberId || undefined,
        insuranceGroupNumber: form.insuranceGroupNumber || undefined,
        emergencyContactName: form.emergencyName || undefined,
        emergencyContactPhone: form.emergencyPhone || undefined,
        primaryProviderId: patient.primaryProviderId || undefined,
        flags: patient.flags || [],
        tags: [],
        allergies,
        problems,
        medications,
      };
      const res = await fetch(`${API_BASE}/patients/${patient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed ${res.status}`);
      let resolvedAvatarUrl: string | null = avatarUrl || null;
      try {
        const latestRes = await fetch(`${API_BASE}/patients/${patient.id}`, {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (latestRes.ok) {
          const latestPatient = (await latestRes.json()) as { avatarUrl?: string | null };
          resolvedAvatarUrl = typeof latestPatient.avatarUrl === 'string' ? latestPatient.avatarUrl : null;
        }
      } catch {
        // Keep locally uploaded URL if refresh read fails.
      }
      const updated: PatientRow = {
        ...patient,
        ...form,
        dob: form.dob,
        phone: form.phone,
        email: form.email,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        country: form.country,
        formattedAddress: form.formattedAddress,
        googlePlaceId: form.googlePlaceId,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        source: form.source,
        avatarUrl: resolvedAvatarUrl,
        insuranceProvider: form.insuranceProvider,
        insuranceMemberId: form.insuranceMemberId,
        insuranceGroupNumber: form.insuranceGroupNumber,
        emergencyContactName: form.emergencyName,
        emergencyContactPhone: form.emergencyPhone,
        genderIdentity: form.genderIdentity,
        allergiesRaw: allergies,
        problemsRaw: problems,
        medicationsRaw: medications,
        allergies: formatAllergies(allergies),
        problems: formatProblems(problems),
        medications: formatMedications(medications),
        address: [form.formattedAddress || form.addressLine1, form.addressLine2, form.city, form.state, form.postalCode, form.country].filter(Boolean).join(', '),
      };
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err.message || tr('Failed to save'));
    } finally {
      setSaving(false);
    }
  }

  function addAllergy(entry: AllergyEntry) {
    if (!entry.name.trim()) return;
    setAllergies((prev) => [...prev, entry]);
  }
  function addProblem(entry: ProblemEntry) {
    if (!entry.name.trim()) return;
    setProblems((prev) => [...prev, entry]);
  }
  function addMedication(entry: MedicationEntry) {
    if (!entry.name.trim()) return;
    setMedications((prev) => [...prev, entry]);
    setMedQuery('');
    setMedSuggestions([]);
  }

  return (
    <div className="fixed inset-0 bg-black/5 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-soft p-6 space-y-4 max-h-[80vh] overflow-auto border border-slate-100">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold text-ink">{tr('Edit patient')}</p>
          <button className="text-sm text-slate-500" onClick={onClose}>
            {tr('Close')}
          </button>
        </div>
        <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <InitialAvatar name={`${patient.firstName} ${patient.lastName}`} src={avatarUrl} alt={tr('Patient avatar')} className="h-16 w-16 text-lg" />
          <div className="space-y-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-slate-400">
              {uploadingAvatar ? tr('Uploading...') : tr('Upload photo')}
              <input
                type="file"
                className="hidden"
                accept="image/*"
                disabled={uploadingAvatar || saving}
                onChange={(event) => {
                  const selected = event.target.files?.[0];
                  event.currentTarget.value = '';
                  void uploadAvatar(selected);
                }}
              />
            </label>
            {avatarUploadError && <p className="text-xs text-rose-600">{avatarUploadError}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <TextField label="MRN" value={form.mrn} onChange={(v) => setForm((f) => ({ ...f, mrn: v }))} />
          <TextField label={tr('DOB')} type="date" value={form.dob} onChange={(v) => setForm((f) => ({ ...f, dob: v }))} />
          <TextField label={tr('Email')} value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
          <TextField label={tr('Phone')} value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
          <div className="col-span-2">
            <AddressFields
              value={{
                line1: form.addressLine1,
                line2: form.addressLine2,
                city: form.city,
                state: form.state,
                postalCode: form.postalCode,
                country: form.country,
                formattedAddress: form.formattedAddress,
                googlePlaceId: form.googlePlaceId,
                latitude: form.latitude ? Number(form.latitude) : null,
                longitude: form.longitude ? Number(form.longitude) : null,
                source: form.source as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
              }}
              onChange={(next) =>
                setForm((prev) => ({
                  ...prev,
                  addressLine1: next.line1,
                  addressLine2: next.line2,
                  city: next.city,
                  state: next.state,
                  postalCode: next.postalCode,
                  country: next.country,
                  formattedAddress: next.formattedAddress ?? '',
                  googlePlaceId: next.googlePlaceId ?? '',
                  latitude: next.latitude != null ? String(next.latitude) : '',
                  longitude: next.longitude != null ? String(next.longitude) : '',
                  source: (next.source ?? 'MANUAL') as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
                }))
              }
              labels={{
                line1: tr('Address Line 1'),
                line2: tr('Address Line 2'),
                city: tr('City / Location'),
                state: tr('State'),
                postalCode: tr('Postal code'),
                country: tr('Country'),
              }}
            />
          </div>
          <SelectField label={tr('Sex')} value={form.sex} options={['M', 'F', 'O']} onChange={(v) => setForm((f) => ({ ...f, sex: v as Sex }))} />
        </div>

        <div className="space-y-3">
          <TextField label={tr('Insurance provider')} value={form.insuranceProvider} onChange={(v) => setForm((f) => ({ ...f, insuranceProvider: v }))} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label={tr('Insurance member ID')} value={form.insuranceMemberId} onChange={(v) => setForm((f) => ({ ...f, insuranceMemberId: v }))} />
            <TextField label={tr('Insurance group #')} value={form.insuranceGroupNumber} onChange={(v) => setForm((f) => ({ ...f, insuranceGroupNumber: v }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label={tr('Emergency contact name')} value={form.emergencyName} onChange={(v) => setForm((f) => ({ ...f, emergencyName: v }))} />
            <TextField label={tr('Emergency contact phone')} value={form.emergencyPhone} onChange={(v) => setForm((f) => ({ ...f, emergencyPhone: v }))} />
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-sm font-semibold text-ink">{tr('Allergies')}</p>
          <div className="flex flex-wrap gap-2">
            {allergies.map((a, idx) => (
              <span
                key={`${a.name}-${idx}`}
                className="px-3 py-1 rounded-full border border-slate-200 bg-slate-50 flex items-center gap-2"
              >
                {a.name}
                <button onClick={() => setAllergies((prev) => prev.filter((_, i) => i !== idx))} className="text-xs text-slate-500 hover:text-rose-500">
                  <Trash2 size={14} />
                </button>
              </span>
            ))}
            <button className="px-2 py-1 rounded border border-slate-200 text-xs" onClick={() => addAllergy({ name: tr('New allergy') })}>
              {tr('Quick add')}
            </button>
          </div>

          <p className="text-sm font-semibold text-ink">{tr('Problems')}</p>
          <div className="flex flex-wrap gap-2">
            {problems.map((a, idx) => (
              <span
                key={`${a.name}-${idx}`}
                className="px-3 py-1 rounded-full border border-slate-200 bg-slate-50 flex items-center gap-2"
              >
                {a.name}
                <button onClick={() => setProblems((prev) => prev.filter((_, i) => i !== idx))} className="text-xs text-slate-500 hover:text-rose-500">
                  <Trash2 size={14} />
                </button>
              </span>
            ))}
            <button className="px-2 py-1 rounded border border-slate-200 text-xs" onClick={() => addProblem({ name: tr('New problem') })}>
              {tr('Quick add')}
            </button>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-ink">{tr('Medications')}</p>
            <div className="flex flex-wrap gap-2">
              {medications.map((a, idx) => (
                <span
                  key={`${a.name}-${idx}`}
                  className="px-3 py-1 rounded-full border border-slate-200 bg-slate-50 flex items-center gap-2"
                >
                  {a.name}
                  <button onClick={() => setMedications((prev) => prev.filter((_, i) => i !== idx))} className="text-xs text-slate-500 hover:text-rose-500">
                    <Trash2 size={14} />
                  </button>
                </span>
              ))}
            </div>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={medQuery}
              placeholder={tr('Search medication to add')}
              onChange={(e) => setMedQuery(e.target.value)}
            />
            {medSuggestions.length > 0 && (
              <div className="border border-slate-200 rounded-lg divide-y max-h-40 overflow-auto">
                {medSuggestions.map((m) => (
                  <button
                    key={m}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    onClick={() => addMedication({ name: m })}
                  >
                    {m}
                  </button>
                ))}
              </div>
            )}
            <button className="px-2 py-1 rounded border border-slate-200 text-xs" onClick={() => addMedication({ name: tr('New medication') })}>
              {tr('Quick add')}
            </button>
          </div>
        </div>

        {error && <div className="text-sm text-rose-600">{error}</div>}

        <div className="flex justify-end gap-2">
          <button className="px-3 py-2 rounded-lg border border-slate-200" onClick={onClose}>
            {tr('Cancel')}
          </button>
          <button className="px-3 py-2 rounded-lg bg-ink text-white" onClick={handleSave} disabled={saving}>
            {saving ? tr('Saving...') : tr('Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ title, icon, items, action }: { title: string; icon: ReactNode; items: string[]; action?: ReactNode }) {
  const { tr } = useI18n();
  return (
    <div className="p-3 rounded-lg border border-slate-100 bg-white shadow-soft/50 relative">
      <div className="flex items-center justify-between text-sm font-semibold text-ink">
        <div className="flex items-center gap-2">
          {icon}
          {title}
        </div>
        {action}
      </div>
      <div className="mt-2 text-sm text-slate-600 space-y-1">
        {items.length ? items.map((i) => <div key={i}>{i}</div>) : <div className="text-slate-500">{tr('None listed')}</div>}
      </div>
    </div>
  );
}

function ManageListCard({
  title,
  icon,
  items,
  onAdd,
  onDelete,
  placeholder,
  options = [],
}: {
  title: string;
  icon: ReactNode;
  items: string[];
  onAdd: (val: string) => void;
  onDelete: (idx: number) => void;
  placeholder: string;
  options?: string[];
}) {
  const { tr } = useI18n();
  const [value, setValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const suggestions = value.trim() ? options.filter(o => o.toLowerCase().includes(value.toLowerCase())).slice(0, 5) : [];

  useEffect(() => {
    if (!dialogOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDialogOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen]);

  function addValue(nextValue?: string) {
    const next = (nextValue ?? value).trim();
    if (!next) return;
    onAdd(next);
    setValue('');
    setShowSuggestions(false);
    setDialogOpen(false);
  }

  return (
    <>
      <div className="p-3 rounded-lg border border-slate-100 bg-white shadow-soft/50 space-y-2 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink">
            {icon}
            {title}
          </div>
          <button className="text-xs px-2 py-1 rounded border border-slate-200 hover:border-ink" onClick={() => setDialogOpen(true)}>
            {tr('Add')}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {items.map((i, idx) => (
            <span key={`${i}-${idx}`} className="px-3 py-1 rounded-full border border-slate-200 bg-slate-50 flex items-center gap-2 text-sm">
              {i}
              <button onClick={() => onDelete(idx)} className="text-xs text-slate-500 hover:text-rose-500">
                <Trash2 size={14} />
              </button>
            </span>
          ))}
          {!items.length && <span className="text-sm text-slate-500">{tr('None listed')}</span>}
        </div>
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setDialogOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
                <h4 className="text-lg font-semibold text-slate-900">{tr('Add')} {title}</h4>
              </div>
              <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700" onClick={() => setDialogOpen(false)}>
                {tr('Close')}
              </button>
            </div>
            <div className="relative">
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={value}
                placeholder={placeholder}
                onChange={(e) => {
                  setValue(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addValue();
                  }
                }}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-10 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-auto">
                  {suggestions.map(s => (
                    <div
                      key={s}
                      className="px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer text-slate-700"
                      onMouseDown={() => addValue(s)}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" onClick={() => setDialogOpen(false)}>
                {tr('Cancel')}
              </button>
              <button className="rounded-lg bg-ink px-3 py-2 text-sm text-white" onClick={() => addValue()} disabled={!value.trim()}>
                {tr('Add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MiniSparkline({ data }: { data: Array<{ label: string; value: number }> }) {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.value));
  const points = data.map((d, idx) => {
    const x = (idx / Math.max(1, data.length - 1)) * 100;
    const y = max ? 100 - (d.value / max) * 100 : 100;
    return `${x},${y}`;
  });
  return (
    <div className="p-3 border border-slate-100 rounded-lg bg-white">
      <svg viewBox="0 0 100 40" className="w-full h-16 text-ink/70">
        <polyline fill="none" stroke="currentColor" strokeWidth="2" points={points.join(' ')} />
      </svg>
      <div className="flex gap-2 text-xs text-slate-500">
        {data.map((d) => (
          <span key={d.label}>
            {d.label}: {d.value}
          </span>
        ))}
      </div>
    </div>
  );
}

function TimelineRow({ title, detail, date, onEdit }: { title: string; detail: string; date: string; onEdit?: () => void }) {
  const { tr } = useI18n();
  return (
    <div className="grid grid-cols-[12px_1fr] gap-3 relative group">
      <div className="flex justify-center">
        <div className="w-px bg-emerald-200 absolute inset-y-0 left-[5px]" />
        <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 relative z-10 mt-1" />
      </div>
      <div className="pb-3 border-b border-slate-100 flex justify-between items-start">
        <div>
          <p className="font-semibold text-sm text-ink">{title}</p>
          <p className="text-sm text-slate-600">{detail}</p>
          <p className="text-xs text-slate-400">{date}</p>
        </div>
        {onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="text-slate-400 hover:text-emerald-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            title={tr('Edit event')}
          >
            <Pill size={14} className="sr-only" />
            {/* Reusing existing icon or new pencil */}
            <span className="sr-only">{tr('Edit')}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-semibold text-ink">{title}</p>
      <div>{children}</div>
    </div>
  );
}

function CalendarBadge() {
  const { tr } = useI18n();
  return (
    <span className="inline-flex items-center gap-1 pill pill-info">
      <ClipboardList size={14} />
      {tr('Upcoming')}
    </span>
  );
}
